import React, { useState, useRef, useEffect } from "react";
import {
	Home,
	Search,
	Menu,
	User,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Edit2,
	MessageSquare,
	Plus,
	X,
	Download,
	Check,
	Mail,
	History,
} from "lucide-react";
import { CraftButton } from "@craft-design-system/core-components-react";
import Header from "../components/Headers/Header";
import SerieTrackingDataTable from "../components/Datatables/SerieTrackingDataTable";
import SerieSidebar from "../components/Sidebars/SerieSidebar";
import SerieTrackingJoyrideTour from "../components/JoyrideTours/SerieTrackingJoyrideTour";
import ExportModal from "../components/Modals/ExportModal";
import SerieExportModal from "../components/Modals/SerieExportModal";
import ReminderEmailModal from "../components/Modals/ReminderEmailModal";
import EmailHistoryModal from "../components/Modals/EmailHistoryModal";
import SerieBulkEditModal from "../components/Modals/SerieBulkEditModal";
import { BorderStyle } from "@mui/icons-material";
import { Box, CircularProgress, Typography } from "@mui/material";
import _ from "lodash";
import JSZip from "jszip";
import { fetchCachedFilterOptions } from "../services/cacheService";
import { getApiBaseUrl } from "../utils/apiConfig";
import { apiClient } from "../services/api";
import {
	shouldShowTour,
	markTourCompleted,
	PAGE_NAMES,
} from "../utils/firstTimeVisit";
import { signOut } from "../utils/authorizationUtils";

// Control flag to enable/disable bulk edit for "Both" view type
const ENABLE_BULK_EDIT_FOR_BOTH_VIEW = false;

// Detect dashboard navigation OR email reminder link at render time (before any effects
// run) so we can pass skipInitialFetch to the data table and prevent the unfiltered
// mount fetch from racing with the dashboard/email-filtered fetch.
const isFromDashboard = () => {
	try {
		const params = new URLSearchParams(window.location.search);
		return params.get("from_dashboard") === "true" || !!params.get("kast_keys");
	} catch {
		return false;
	}
};

const PartsTrackingTool = ({ user }) => {
	useEffect(() => {
		document.title = "MAN PTT - Series";
	}, []);
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const [selectedProject, setSelectedProject] = useState([]);
	const [selectedProgramm, setSelectedProgramm] = useState([]);
	const [selectedViewType, setSelectedViewType] = useState("Prototype"); // Keep this state here  // Get user display name function
	const getUserDisplayName = () => {
		if (user?.profile?.preferred_username) {
			return user.profile.preferred_username;
		}
		if (user?.profile?.email) {
			return user.profile.email.split("@")[0];
		}
		return "User";
	};

	// Get full user email for authorization and email features
	const getUserEmail = () => {
		if (user?.profile?.email) {
			return user.profile.email;
		}
		if (
			user?.profile?.preferred_username &&
			user.profile.preferred_username.includes("@")
		) {
			return user.profile.preferred_username;
		}

		// Fallback to localStorage username if it contains an email
		const localStorageUsername = localStorage.getItem("username");
		if (localStorageUsername && localStorageUsername.includes("@")) {
			return localStorageUsername;
		}

		return "user@example.com"; // Default fallback
	};
	const [selectedPurchaseType, setSelectedPurchaseType] = useState([]);
	const [selectedSeTeams, setSelectedSeTeams] = useState([]);
	const [selectedGxClassification, setSelectedGxClassification] = useState([]);
	const [selectedRampUpGroup, setSelectedRampUpGroup] = useState([]);
	const [selectedBemiErf, setSelectedBemiErf] = useState([]);
	const [selectedFlag, setSelectedFlag] = useState([]);
	const [selectedProjectNo, setSelectedProjectNo] = useState([]);
	const [selectedAlg, setSelectedAlg] = useState([]);
	const [selectedProgrammFilter, setSelectedProgrammFilter] = useState([]);
	const [selectedChangeNo, setSelectedChangeNo] = useState([]);
	const [selectedSupplier, setSelectedSupplier] = useState([]);
	const [selectedSnr, setSelectedSnr] = useState([]);
	const [selectedResponsible, setSelectedResponsible] = useState([]);
	const [selectedFollowupDate, setSelectedFollowupDate] = useState({
		from: null,
		to: null,
	});
	const [selectedBza, setSelectedBza] = useState([]);
	const [selectedA80Status, setSelectedA80Status] = useState([]);
	const [selectedE99Status, setSelectedE99Status] = useState([]);
	const [selectedP99Status, setSelectedP99Status] = useState([]);
	const [selectedQi000Status, setSelectedQi000Status] = useState([]);
	const [selectedQi100Status, setSelectedQi100Status] = useState([]);
	const [selectedQi500Status, setSelectedQi500Status] = useState([]);
	const [selectedQi900Status, setSelectedQi900Status] = useState([]);
	const [selectedLr99Status, setSelectedLr99Status] = useState([]);
	const [selectedMr99Status, setSelectedMr99Status] = useState([]);
	const [selectedPpapStatus, setSelectedPpapStatus] = useState([]);
	const [selectedWfIndex, setSelectedWfIndex] = useState([]);
	const [selectedPartStatus, setSelectedPartStatus] = useState([]);
	const [selectedPartNoPrefix, setSelectedPartNoPrefix] = useState([]);
	const [selectedBttStatus, setSelectedBttStatus] = useState([]);
	const [selectedWfAlg, setSelectedWfAlg] = useState([]);
	const [selectedWfStufe, setSelectedWfStufe] = useState([]);
	const [selectedWfBza, setSelectedWfBza] = useState([]);
	const [selectedFetFrom, setSelectedFetFrom] = useState(null);
	const [selectedFetTo, setSelectedFetTo] = useState(null);
	const [selectedPetFrom, setSelectedPetFrom] = useState(null);
	const [selectedPetTo, setSelectedPetTo] = useState(null);
	const [selectedSofreiErf, setSelectedSofreiErf] = useState([]);
	const [selectedSofreiEndeFrom, setSelectedSofreiEndeFrom] = useState(null);
	const [selectedSofreiEndeTo, setSelectedSofreiEndeTo] = useState(null);
	const [selectedTags, setSelectedTags] = useState([]);
	// kast_keys pinned from an email reminder link — persisted so filters applied on top don't lose them
	const [emailKastKeys, setEmailKastKeys] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedSeTeam, setSelectedSeTeam] = useState([]);
	const [searchInput, setSearchInput] = useState("");
	const [searchError, setSearchError] = useState("");
	const [itemsPerPage, setItemsPerPage] = useState(100);
	const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
	const [selectedItems, setSelectedItems] = useState([]);
	const [allSelected, setAllSelected] = useState(false);
	const [showFilterTooltip, setShowFilterTooltip] = useState(false);
	const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(true);
	const [filterOptionsError, setFilterOptionsError] = useState(null);
	// Tour state
	const [isTourOpen, setIsTourOpen] = useState(false);

	// Export state
	const [isExporting, setIsExporting] = useState(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);
	// Email modal states
	const [isReminderEmailModalOpen, setIsReminderEmailModalOpen] =
		useState(false);
	const [isEmailHistoryModalOpen, setIsEmailHistoryModalOpen] = useState(false);
	// Serie Bulk Edit Modal states
	const [isSerieBulkEditModalOpen, setIsSerieBulkEditModalOpen] =
		useState(false);
	const [selectedItemsForSerieBulkEdit, setSelectedItemsForSerieBulkEdit] =
		useState([]);
	const [isSerieBulkSaving, setIsSerieBulkSaving] = useState(false);
	const [serieBulkProgress, setSerieBulkProgress] = useState(null);
	const [isDashboardFilterLoading, setIsDashboardFilterLoading] =
		useState(false);
	const dashboardFiltersApplied = useRef(false);
	// Stores the filters applied from dashboard navigation so SerieSidebar can be pre-populated.
	const [dashboardInitialFilters, setDashboardInitialFilters] = useState({});
	// Incrementing this key forces SerieSidebar to remount (and reset its internal state)
	// whenever the user clears all filters from the parent.
	const [sidebarResetKey, setSidebarResetKey] = useState(0);

	// Prevent duplicate API calls in React.StrictMode (development only)
	const hasInitialized = useRef(false);

	const [filterOptions, setFilterOptions] = useState({
		projectNumbers: [],
		algValues: [],
		programmValues: [],
		changeNumbers: [],
		snrValues: [],
		supplierValues: [],
		responsibleValues: [],
		seTeamValues: [],
		followupDateValues: [],
		bzaValues: [],
		a80StatusValues: [],
		e99StatusValues: [],
		p99StatusValues: [],
		qi000StatusValues: [],
		qi100StatusValues: [],
		qi500StatusValues: [],
		qi900StatusValues: [],
		lr99StatusValues: [],
		mr99StatusValues: [],
		ppapStatusValues: [],
		wfIndexValues: [],
		statusToggleValues: [],
		wfAlgValues: [],
		wfStufeValues: [],
		wfBzaValues: [],
		projectAlgMapping: {},
		projectChangeMapping: {},
		programmAlgMapping: {},
		programmChangeMapping: {},
		projectSeTeamMapping: {},
		programmSeTeamMapping: {},
		projectBzaMapping: {},
		programmBzaMapping: {},
		projectPpapMapping: {},
		programmPpapMapping: {},
		projectWfIndexMapping: {},
		programmWfIndexMapping: {},
		projectSupplierMapping: {},
		programmSupplierMapping: {},
		projectResponsibleMapping: {},
		programmResponsibleMapping: {},
		sofreiErfValues: [],
		tagsValues: [],
	});
	const [editOptions, setEditOptions] = useState({
		projects: [],
		purchaseTypes: [],
		purchaseTypeSystems: [],
		total_req: [],
		seTeams: [],
		gxClassifications: [],
		rampUpGroups: [],
		bemiErf: [],
		suppliers: [],
	});
	const dataTableRef = useRef();

	// State for column visibility
	const [columnVisibility, setColumnVisibility] = useState({
		rampUpGroup: true,
		seTeams: true,
		purchaseType: true,
		gxClassification: true,
		supplier: true,
	});

	const handleColumnVisibilityChange = (newVisibility) => {
		setColumnVisibility(newVisibility);
	};

	// Handle selection of items in the data table
	const handleSelectItems = (items) => {
		setSelectedItems(items);
		setAllSelected(
			items.length > 0 &&
				items.length === (dataTableRef.current?.getAllData()?.length || 0),
		);
	};

	// Edit mode state
	const [isEditMode, setIsEditMode] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	// Handle enabling edit mode
	const handleEnableEdit = () => {
		if (dataTableRef.current && dataTableRef.current.enableEditMode) {
			dataTableRef.current.enableEditMode();
			setIsEditMode(true);
		}
	};

	// Handle canceling edit mode
	const handleCancelEdit = () => {
		if (dataTableRef.current && dataTableRef.current.cancelEditMode) {
			dataTableRef.current.cancelEditMode();
			setIsEditMode(false);
		}
	};

	// Handle saving changes
	const handleSaveChanges = async () => {
		try {
			setIsSaving(true);
			if (dataTableRef.current && dataTableRef.current.bulkSave) {
				await dataTableRef.current.bulkSave();
				setIsEditMode(false);
			}
		} catch (error) {
			console.error("Error saving changes:", error);
			alert("Failed to save changes. Please try again.");
		} finally {
			setIsSaving(false);
		}
	};
	// Optimized data loading sequence for better performance
	useEffect(() => {
		// Prevent duplicate calls in React.StrictMode (development only)
		if (hasInitialized.current) {
			return;
		}
		hasInitialized.current = true;
		// Check if tour should continue from landing page
		const shouldContinueTour = localStorage.getItem("continueSeriesTour");
		if (shouldContinueTour === "true") {
			// Clear the flag and start the tour after a brief delay
			localStorage.removeItem("continueSeriesTour");
			setTimeout(() => {
				setIsTourOpen(true);
				markTourCompleted(PAGE_NAMES.SERIES);
			}, 1000);
		} else if (shouldShowTour(PAGE_NAMES.SERIES)) {
			// First time visiting Series Tracking page directly
			setTimeout(() => {
				setIsTourOpen(true);
				markTourCompleted(PAGE_NAMES.SERIES);
			}, 1500);
		}

		const fetchDataAndOptions = async () => {
			try {
				setIsFilterOptionsLoading(true);
				setFilterOptionsError(null);

				const defaultFilters = {
					project: [],
					programm: [],
					purchaseType: [],
					seTeams: [],
					gxClassification: [],
					rampUpGroup: [],
					bemiErf: [],
				}; // Try to get series filter options from cache first
				let filterData;
				try {
					console.log("Trying to load series filter options from S3 cache...");
					filterData = await apiClient.get("/api/series-filter-options");
					filterData = filterData.data;
					console.log("Successfully loaded series filter options from API");
				} catch (cacheError) {
					console.log("Failed to load series filter options from API");
					setFilterOptionsError("Failed to load filter options");
					filterData = {};
				}
				setFilterOptions({
					projectNumbers: filterData.projectNumbers || [],
					algValues: filterData.algValues || [],
					programmValues: filterData.programmValues || [],
					changeNumbers: filterData.changeNumbers || [],
					snrValues: filterData.snrValues || [],
					supplierValues: filterData.supplierValues || [],
					responsibleValues: filterData.responsibleValues || [],
					seTeamValues: filterData.seTeamValues || [],
					followupDateValues: filterData.followupDateValues || [],
					bzaValues: filterData.bzaValues || [],
					a80StatusValues: filterData.a80StatusValues || [],
					e99StatusValues: filterData.e99StatusValues || [],
					p99StatusValues: filterData.p99StatusValues || [],
					qi000StatusValues: filterData.qi000StatusValues || [],
					qi100StatusValues: filterData.qi100StatusValues || [],
					qi500StatusValues: filterData.qi500StatusValues || [],
					qi900StatusValues: filterData.qi900StatusValues || [],
					lr99StatusValues: filterData.lr99StatusValues || [],
					mr99StatusValues: filterData.mr99StatusValues || [],
					ppapStatusValues: filterData.ppapStatusValues || [],
					wfIndexValues: filterData.wfIndexValues || [],
					statusToggleValues: filterData.statusToggleValues || [],
					wfAlgValues: filterData.wfAlgValues || [],
					wfStufeValues: filterData.wfStufeValues || [],
					wfBzaValues: filterData.wfBzaValues || [], // Dynamic filter mappingsfor series data
					projectAlgMapping: filterData.projectAlgMapping || {},
					projectChangeMapping: filterData.projectChangeMapping || {},
					programmAlgMapping: filterData.programmAlgMapping || {},
					programmChangeMapping: filterData.programmChangeMapping || {},
					// Dynamic filter mappings for Se-Team, BZA, PPAP, wfIndex, Supplier, Responsible
					projectSeTeamMapping: filterData.projectSeTeamMapping || {},
					programmSeTeamMapping: filterData.programmSeTeamMapping || {},
					projectBzaMapping: filterData.projectBzaMapping || {},
					programmBzaMapping: filterData.programmBzaMapping || {},
					projectPpapMapping: filterData.projectPpapMapping || {},
					programmPpapMapping: filterData.programmPpapMapping || {},
					projectWfIndexMapping: filterData.projectWfIndexMapping || {},
					programmWfIndexMapping: filterData.programmWfIndexMapping || {},
					projectSupplierMapping: filterData.projectSupplierMapping || {},
					programmSupplierMapping: filterData.programmSupplierMapping || {},
					projectResponsibleMapping: filterData.projectResponsibleMapping || {},
					programmResponsibleMapping:
						filterData.programmResponsibleMapping || {},
					sofreiErfValues: filterData.sofreiErfValues || [],
					tagsValues: filterData.tagsValues || [],
				});

				console.log("✅ Series Filter Options Set:", {
					supplierCount: filterData.supplierValues?.length || 0,
					suppliers: filterData.supplierValues?.slice(0, 5) || [],
				});

				setEditOptions({
					projects: filterData.projects || [],
					purchaseTypes: filterData.purchaseTypes || [],
					purchaseTypeSystems: filterData.purchaseTypeSystems || [],
					total_req: filterData.total_req || [],
					seTeams: filterData.seTeams || [],
					gxClassifications: filterData.gxClassifications || [],
					rampUpGroups: filterData.rampUpGroups || [],
					bemiErf: filterData.bemiErf || [],
					suppliers: filterData.suppliers || [],
				});

				// Remove this call - let PTSachNrDataTable handle its own initialization
				// if (dataTableRef.current && dataTableRef.current.fetchData) {
				//   await dataTableRef.current.fetchData(defaultFilters, 1, itemsPerPage, selectedViewType);
				//   setSelectedProject(defaultFilters.project);
				// }
			} catch (error) {
				console.error("Error fetching data and options:", error);
				setFilterOptionsError("Failed to load filter options");
			} finally {
				setIsFilterOptionsLoading(false);
			}
		};
		fetchDataAndOptions();

		// Apply dashboard click-through filters if navigated from the Series Dashboard
		applyDashboardFilters();
	}, []);

	// Check for first-time visit
	useEffect(() => {
		if (shouldShowTour(PAGE_NAMES.SERIES)) {
			// Small delay to ensure page is fully loaded
			const timer = setTimeout(() => {
				setIsTourOpen(true);
				markTourCompleted(PAGE_NAMES.SERIES);
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, []);

	const fontStyles = {
		regular: {
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		bold: {
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
		},
	};
	const styles = {
		mainContainer: {
			display: "flex",
			flexDirection: "column",
			height: "100vh",
			width: "100vw",
			paddingTop: "calc(74px + var(--banner-height, 0px))",
			boxSizing: "border-box",
			backgroundColor: "#f0f0f0",
			overflow: "hidden",
			...fontStyles.regular,
		},
		contentWrapper: {
			display: "flex",
			flex: 1,
			overflow: "hidden",
			padding: "0 0px 0px 0px", // Removed right padding to allow full width
		},
		mainContent: {
			flex: 1,
			overflow: "hidden",
			backgroundColor: "white",
			marginLeft: "0px",
			display: "flex",
			flexDirection: "column",
		},
		tableContainer: {
			flex: 1,
			overflow: "auto",
			padding: "0px",
			width: "100%",
			display: "flex",
			flexDirection: "column",
			minHeight: 0,
		},
		searchContainer: {
			padding: "10px 0px 5px 0px",
			backgroundColor: "#f1f1f1",
			borderBottom: "1px solid #eee",
			width: "100%",
		},
		modal: {
			position: "fixed",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			zIndex: 1000,
		},
		modalContent: {
			backgroundColor: "white",
			width: "80%",
			maxWidth: "1000px",
			maxHeight: "90vh",
			display: "flex",
			flexDirection: "column",
			boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
		},
		modalHeader: {
			padding: "0px 20px",
			borderBottom: "1px solid #eee",
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			backgroundColor: "#f5f5f5",
		},
		modalBody: {
			padding: "15px 10px",
			overflowY: "auto",
			flex: 1,
		},
		modalFooter: {
			padding: "10px 20px",
			borderTop: "1px solid #eee",
			display: "flex",
			justifyContent: "flex-end",
			gap: "10px",
			backgroundColor: "#f5f5f5",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			marginBottom: "20px",
		},
		tableHeader: {
			...fontStyles.bold,
			padding: "12px",
			textAlign: "left",
			fontSize: "12px",
			color: "#666",
		},
		tableCell: {
			...fontStyles.regular,
			padding: "12px",
			textAlign: "left",
		},
		select: {
			...fontStyles.regular,
			width: "100%",
			padding: "8px",
			border: "1px solid #ddd",
			backgroundColor: "white",
		},
		button: {
			...fontStyles.regular,
			backgroundColor: "#666",
			color: "white",
			border: "none",
			padding: "8px 16px",
			cursor: "pointer",
		},
		label: {
			...fontStyles.regular,
			fontSize: "14px",
			color: "#333",
		},
	};

	const sidebarStyles = {
		container: {
			width: isSidebarCollapsed ? "50px" : "250px",
			backgroundColor: "#f0f0f0",
			padding: 0,
			transition: "all 0.3s ease",
			position: "relative",
			overflow: "hidden",
			padding: "0px 0px 10px 0px",
			borderRight: "2px solid black",
			borderTop: "2px solid black",
			height: "100%",
			...fontStyles.regular,
		},
		header: {
			display: "flex",
			alignItems: "center",
			padding: "15px",
			backgroundColor: "#f0f0f0",
			justifyContent: isSidebarCollapsed ? "center" : "space-between",
		},
		title: {
			...fontStyles.bold,
			fontSize: "16px",
			margin: 0,
			display: "flex",
			alignItems: "center",
		},
		toggleButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
		},
		content: {
			opacity: isSidebarCollapsed ? 0 : 1,
			visibility: isSidebarCollapsed ? "hidden" : "visible",
			transition: "opacity 0.2s ease",
			width: "100%",
			padding: "15px",
		},
		filterLabel: {
			...fontStyles.regular,
			display: "block",
			fontSize: "14px",
			marginBottom: "6px",
			color: "#333",
		},
		select: {
			...fontStyles.regular,
			width: "90%",
			padding: "8px",
			border: "1px solid #ddd",
			marginBottom: "16px",
			backgroundColor: "white",
			cursor: "pointer",
		},
		filterGroup: {
			marginBottom: "15px",
		},
	};

	const handleSidebarToggle = () => {
		setIsSidebarCollapsed(!isSidebarCollapsed);
	};
	// Function to get current sidebar filters
	const getCurrentFilters = () => {
		return {
			projectNo: selectedProjectNo || [],
			alg: selectedAlg || [],
			programm: selectedProgrammFilter || [],
			changeNo: selectedChangeNo || [],
			supplier: selectedSupplier || [],
			snr: selectedSnr || [],
			responsible: selectedResponsible || [],
			seTeam: selectedSeTeam || [],
			followupDateFrom: selectedFollowupDate?.from || null,
			followupDateTo: selectedFollowupDate?.to || null,
			bza: selectedBza || [],
			a80Status: selectedA80Status || [],
			e99Status: selectedE99Status || [],
			p99Status: selectedP99Status || [],
			qi000Status: selectedQi000Status || [],
			qi100Status: selectedQi100Status || [],
			qi500Status: selectedQi500Status || [],
			qi900Status: selectedQi900Status || [],
			lr99Status: selectedLr99Status || [],
			mr99Status: selectedMr99Status || [],
			ppapStatus: selectedPpapStatus || [],
			wfIndex: selectedWfIndex || [],
			partStatus: selectedPartStatus || [],
			partNoPrefix: selectedPartNoPrefix || [],
			bttStatus: selectedBttStatus || [],
			fetFrom: selectedFetFrom || null,
			fetTo: selectedFetTo || null,
			petFrom: selectedPetFrom || null,
			petTo: selectedPetTo || null,
			tags: selectedTags || [],
		};
	};
	// Debounce search to avoid too many requests
	const debouncedSearch = React.useCallback(
		_.debounce((query) => {
			if (dataTableRef.current && dataTableRef.current.fetchData) {
				// Combine current sidebar filters with search query
				const currentFilters = getCurrentFilters();
				const combinedFilters = {
					...currentFilters,
					search: query,
					wfStufe: selectedWfStufe || [],
					wfBza: selectedWfBza || [],
					wfAlg: selectedWfAlg || [],
					kastKeys: emailKastKeys.length > 0 ? emailKastKeys : [],
				};
				console.log(
					"🔍 Performing search with combined filters:",
					combinedFilters,
				);
				dataTableRef.current.fetchData(
					combinedFilters,
					1,
					itemsPerPage,
					selectedViewType,
				);
			}
		}, 500),
		[
			selectedViewType,
			selectedProjectNo,
			selectedAlg,
			selectedProgrammFilter,
			selectedChangeNo,
			selectedSnr,
			selectedResponsible,
			selectedSeTeam,
			selectedFollowupDate,
			selectedBza,
			selectedA80Status,
			selectedE99Status,
			selectedP99Status,
			selectedQi000Status,
			selectedQi100Status,
			selectedQi500Status,
			selectedQi900Status,
			selectedLr99Status,
			selectedMr99Status,
			selectedPpapStatus,
			selectedWfIndex,
			selectedPartNoPrefix,
			selectedBttStatus,
			selectedWfStufe,
			selectedWfBza,
			selectedWfAlg,
			selectedFetFrom,
			selectedFetTo,
			selectedPetFrom,
			selectedPetTo,
			selectedSofreiErf,
			selectedSofreiEndeFrom,
			selectedSofreiEndeTo,
			itemsPerPage,
			emailKastKeys,
		],
	);

	// Search submit handler for form submission
	const handleSearchSubmit = (e) => {
		e.preventDefault();
		handleSearch(searchInput);
	};

	// Validate search query for special characters
	const validateSearchQuery = (query) => {
		// Allow alphanumeric characters, spaces, hyphens, and dots for part numbers
		// Hyphens and dots are common in part numbers like "6KT-SCHRAUBE M10X35-Z1-10.9-MAN183-B1"
		const regex = /^[a-zA-Z0-9\s\-\.]*$/;
		return regex.test(query);
	};

	// Search handler for direct search
	const handleSearch = async (query) => {
		// Clear previous errors
		setSearchError("");
		console.log(
			"Search initiated with query:",
			query,
			"in view type:",
			selectedViewType,
		);

		// If query is empty, proceed with empty search
		if (!query.trim()) {
			setSearchQuery("");
			setSelectedItems([]);
			setAllSelected(false);
			debouncedSearch("");
			return;
		}

		// Validate query for special characters
		if (!validateSearchQuery(query)) {
			setSearchError(
				"Please use only letters, numbers, and spaces in your search",
			);
			return;
		}

		// Additional validation for query length
		if (query.length > 100) {
			setSearchError(
				"Search query is too long. Maximum 100 characters allowed.",
			);
			return;
		}

		setSearchQuery(query);
		// Clear selected items when search changes
		setSelectedItems([]);
		setAllSelected(false);

		try {
			// Trigger debounced search
			await debouncedSearch(query);
		} catch (error) {
			console.error("Search error:", error);
			setSearchError(
				"An error occurred while searching. Please try again with different terms.",
			);
		}
	};
	const handleSearchClear = () => {
		setSearchInput("");
		setSearchQuery("");
		setSearchError("");
		if (dataTableRef.current && dataTableRef.current.fetchData) {
			const currentFilters = getCurrentFilters();
			const combinedFilters = {
				...currentFilters,
				search: "",
				wfStufe: selectedWfStufe || [],
				wfBza: selectedWfBza || [],
				wfAlg: selectedWfAlg || [],
				kastKeys: emailKastKeys.length > 0 ? emailKastKeys : [],
			};
			console.log("🧹 Clearing search with current filters:", combinedFilters);
			dataTableRef.current.fetchData(
				combinedFilters,
				1,
				itemsPerPage,
				selectedViewType,
			);
		}
	};

	const handleBulkEditOpen = () => {
		setIsBulkEditOpen(true);
	};

	const handleBulkEditClose = () => {
		setIsBulkEditOpen(false);
	};

	const handleBulkEdit = () => {
		setIsBulkEditOpen(true);
	};
	const handleCloseBulkEdit = () => {
		setIsBulkEditOpen(false);
	};

	const handleSaveBulkEdit = () => {
		// Implement save functionality here
		setIsBulkEditOpen(false);
	}; // Function to navigate to Series Dashboard
	const navigateToDashboard = () => {
		window.location.href = "/dashboard?view=series";
	}; // Function to apply dashboard click-through filters from URL params
	const applyDashboardFilters = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const fromDashboard = urlParams.get("from_dashboard");
		const wfStage = urlParams.get("wf_stage"); // human-readable label e.g. "Technik"
		const wfStufeParam = urlParams.get("wf_stufe"); // comma-separated codes e.g. "WF100,WF700"
		const wfBzaParam = urlParams.get("wf_bza"); // comma-separated status e.g. "KT,HT"
		const wfAlgParam = urlParams.get("wf_alg"); // comma-separated wf_alg values e.g. "KT,HT"
		const excludeNotr = urlParams.get("exclude_notr") === "true";
		const excludeBttDeleted = urlParams.get("exclude_btt_deleted") === "true";
		const projectNumberParam = urlParams.get("project_number");
		const algParam = urlParams.get("alg");
		const changeNoParam = urlParams.get("change_no");
		const kastKeysParam = urlParams.get("kast_keys"); // from email reminder link    // Handle email reminder view with specific kast_keys
		if (kastKeysParam) {
			console.log(
				"Applying kast_keys filter from email reminder (series):",
				kastKeysParam,
			);

			// Guard against double-application (same as dashboard path)
			if (dashboardFiltersApplied.current) return;
			dashboardFiltersApplied.current = true;

			const kastKeyList = kastKeysParam
				.split(",")
				.map((k) => k.trim())
				.filter(Boolean);

			// Clean up the URL so refreshing doesn't re-apply the filter
			window.history.replaceState({}, document.title, window.location.pathname);

			// Persist the pinned kast_keys so every subsequent filter call re-sends them
			setEmailKastKeys(kastKeyList);

			setIsDashboardFilterLoading(true);

			const tryApply = () => {
				if (dataTableRef.current && dataTableRef.current.fetchData) {
					const filters = {
						projectNo: [],
						alg: [],
						programm: [],
						changeNo: [],
						supplier: [],
						snr: [],
						responsible: [],
						seTeam: [],
						followupDateFrom: null,
						followupDateTo: null,
						bza: [],
						a80Status: [],
						e99Status: [],
						p99Status: [],
						qi000Status: [],
						qi100Status: [],
						qi500Status: [],
						qi900Status: [],
						ppapStatus: [],
						wfIndex: [],
						partStatus: [],
						partNoPrefix: [],
						wfStufe: [],
						wfBza: [],
						wfAlg: [],
						search: "",
						kastKeys: kastKeyList,
					};

					console.log(
						"Applying kastKeys filter to Series table from email link:",
						kastKeyList,
					);

					const fetchPromise = dataTableRef.current.fetchData(
						filters,
						1,
						itemsPerPage,
					);
					if (fetchPromise && typeof fetchPromise.then === "function") {
						fetchPromise.finally(() => setIsDashboardFilterLoading(false));
					} else {
						setTimeout(() => setIsDashboardFilterLoading(false), 1500);
					}
				} else {
					setTimeout(tryApply, 200);
				}
			};

			setTimeout(tryApply, 300);
			return; // Exit early — kast_keys case handled
		}
		if (!fromDashboard || dashboardFiltersApplied.current) return;
		dashboardFiltersApplied.current = true;

		// Parse comma-separated lists from URL
		const wfStufeList = wfStufeParam
			? wfStufeParam.split(",").filter(Boolean)
			: [];
		const wfBzaList = wfBzaParam ? wfBzaParam.split(",").filter(Boolean) : [];
		const wfAlgList = wfAlgParam ? wfAlgParam.split(",").filter(Boolean) : [];
		const projectNos = projectNumberParam
			? projectNumberParam.split(",").filter(Boolean)
			: [];
		const algList = algParam ? algParam.split(",").filter(Boolean) : [];
		const changeNos = changeNoParam
			? changeNoParam.split(",").filter(Boolean)
			: [];

		// Only proceed if there is at least one meaningful filter
		if (
			wfStufeList.length === 0 &&
			wfBzaList.length === 0 &&
			wfAlgList.length === 0 &&
			projectNos.length === 0 &&
			algList.length === 0 &&
			changeNos.length === 0
		)
			return; // ── VFF-style: set sidebar state directly so active filter chips appear ──
		if (wfStufeList.length > 0) setSelectedWfStufe(wfStufeList);
		if (wfBzaList.length > 0) setSelectedWfBza(wfBzaList);
		if (wfAlgList.length > 0) setSelectedWfAlg(wfAlgList);
		if (algList.length > 0) setSelectedAlg(algList);
		if (projectNos.length > 0) setSelectedProjectNo(projectNos);
		if (changeNos.length > 0) setSelectedChangeNo(changeNos);

		// Pre-populate the sidebar with these dashboard filters so that when the user
		// applies additional sidebar filters they are merged on top, not lost.
		const dashFilters = {
			wfStufe: wfStufeList,
			wfBza: wfBzaList,
			wfAlg: wfAlgList,
			alg: algList,
			projectNo: projectNos,
			changeNo: changeNos,
		};
		setDashboardInitialFilters(dashFilters);
		// Remount the sidebar so it picks up the initialFilters above
		setSidebarResetKey((k) => k + 1);

		// Clean URL immediately (no dashboard params needed after state is set)
		window.history.replaceState({}, document.title, window.location.pathname);

		setIsDashboardFilterLoading(true);

		// Wait for the data table to be ready, then fire the filtered fetch
		const tryApply = () => {
			if (dataTableRef.current && dataTableRef.current.fetchData) {
				const filters = {
					projectNo: projectNos,
					alg: algList,
					programm: [],
					changeNo: changeNos,
					supplier: [],
					snr: [],
					responsible: [],
					seTeam: [],
					followupDateFrom: null,
					followupDateTo: null,
					bza: [],
					a80Status: [],
					e99Status: [],
					p99Status: [],
					qi000Status: [],
					qi100Status: [],
					qi500Status: [],
					qi900Status: [],
					ppapStatus: [],
					wfIndex: [],
					partStatus: [],
					partNoPrefix: [],
					wfStufe: wfStufeList,
					wfBza: wfBzaList,
					wfAlg: wfAlgList,
					excludeNotr: excludeNotr,
					excludeBttDeleted: excludeBttDeleted,
					search: "",
				};

				console.log("🎯 Applying Series Dashboard filters (sidebar state):", {
					wfStage,
					wfStufeList,
					wfBzaList,
					wfAlgList,
					excludeNotr,
					projectNos,
					algList,
					changeNos,
				});

				const fetchPromise = dataTableRef.current.fetchData(
					filters,
					1,
					itemsPerPage,
				);
				if (fetchPromise && typeof fetchPromise.then === "function") {
					fetchPromise.finally(() => setIsDashboardFilterLoading(false));
				} else {
					setTimeout(() => setIsDashboardFilterLoading(false), 1500);
				}
			} else {
				// Data table not mounted yet — retry
				setTimeout(tryApply, 200);
			}
		};

		setTimeout(tryApply, 300);
	};

	// Tour handlers
	const handleStartTour = () => {
		setIsTourOpen(true);
	};
	const handleCloseTour = () => {
		setIsTourOpen(false);
		// Ensure the page is marked as visited when tour is closed
		markTourCompleted(PAGE_NAMES.SERIES);
	};
	// Handle Series data export to Excel with S3 and modal
	const handleExportCSV = async () => {
		try {
			setIsExporting(true);
			// Prepare the current filters for export — must mirror all filters used in data table
			const exportFilters = {
				project_number: selectedProjectNo || [],
				alg: selectedAlg || [],
				change_no: selectedChangeNo || [],
				snr: selectedSnr || [],
				programm: selectedProgrammFilter || [],
				responsible: selectedResponsible || [],
				se_team: selectedSeTeam || [],
				followup_date_from: selectedFollowupDate?.from || null,
				followup_date_to: selectedFollowupDate?.to || null,
				supplier: selectedSupplier || [],
				bza: selectedBza || [],
				snr_prefix: selectedPartNoPrefix || [],
				a80_status: selectedA80Status || [],
				e99_status: selectedE99Status || [],
				p99_status: selectedP99Status || [],
				qi000_status: selectedQi000Status || [],
				qi100_status: selectedQi100Status || [],
				qi500_status: selectedQi500Status || [],
				qi900_status: selectedQi900Status || [],
				wf_alg: selectedWfAlg || [],
				wf_stufe: selectedWfStufe || [],
				wf_bza: selectedWfBza || [],
				lr99_status: selectedLr99Status || [],
				mr99_status: selectedMr99Status || [],
				ppap_status: selectedPpapStatus || [],
				wf_index: selectedWfIndex || [],
				part_status: selectedPartStatus || [],
				btt_status: selectedBttStatus || [],
				search: searchQuery || "",
				fet_from: selectedFetFrom || null,
				fet_to: selectedFetTo || null,
				pet_from: selectedPetFrom || null,
				pet_to: selectedPetTo || null,
				sofrei_erf: selectedSofreiErf || [],
				sofrei_ende_from: selectedSofreiEndeFrom || null,
				sofrei_ende_to: selectedSofreiEndeTo || null,
				tags: selectedTags || [],
			};

			console.log("🔄 Starting Series export with filters:", exportFilters);

			// Count active filters for user feedback
			const activeFiltersCount = Object.values(exportFilters).reduce(
				(count, filterValue) => {
					if (Array.isArray(filterValue)) {
						return count + (filterValue.length > 0 ? 1 : 0);
					} else {
						return count + (filterValue && filterValue.trim() ? 1 : 0);
					}
				},
				0,
			);

			console.log(
				`📊 Export will use ${activeFiltersCount > 0 ? `${activeFiltersCount} active filters (filtered data)` : "no filters (all data)"}`,
			); // Build query parameters for the export API
			const queryParams = new URLSearchParams();

			// Add array filters
			exportFilters.project_number.forEach((p) =>
				queryParams.append("project_number", p),
			);
			exportFilters.alg.forEach((a) => queryParams.append("alg", a));
			exportFilters.change_no.forEach((c) =>
				queryParams.append("change_no", c),
			);
			exportFilters.snr.forEach((s) => queryParams.append("snr", s));
			exportFilters.programm.forEach((p) => queryParams.append("programm", p));
			exportFilters.supplier.forEach((s) => queryParams.append("supplier", s));
			exportFilters.responsible.forEach((r) =>
				queryParams.append("responsible", r),
			);
			exportFilters.se_team.forEach((st) => queryParams.append("se_team", st));
			if (exportFilters.followup_date_from)
				queryParams.append(
					"followup_date_from",
					exportFilters.followup_date_from,
				);
			if (exportFilters.followup_date_to)
				queryParams.append("followup_date_to", exportFilters.followup_date_to);
			exportFilters.bza.forEach((b) => queryParams.append("bza", b));
			exportFilters.snr_prefix.forEach((p) =>
				queryParams.append("snr_prefix", p),
			);
			exportFilters.a80_status.forEach((s) =>
				queryParams.append("a80_status", s),
			);
			exportFilters.e99_status.forEach((s) =>
				queryParams.append("e99_status", s),
			);
			exportFilters.p99_status.forEach((s) =>
				queryParams.append("p99_status", s),
			);
			exportFilters.qi000_status.forEach((s) =>
				queryParams.append("qi000_status", s),
			);
			exportFilters.qi100_status.forEach((s) =>
				queryParams.append("qi100_status", s),
			);
			exportFilters.qi500_status.forEach((s) =>
				queryParams.append("qi500_status", s),
			);
			exportFilters.qi900_status.forEach((s) =>
				queryParams.append("qi900_status", s),
			);
			exportFilters.wf_alg.forEach((s) => queryParams.append("wf_alg", s));
			exportFilters.wf_stufe.forEach((s) => queryParams.append("wf_stufe", s));
			exportFilters.wf_bza.forEach((s) => queryParams.append("wf_bza", s));
			exportFilters.lr99_status.forEach((s) =>
				queryParams.append("lr99_status", s),
			);
			exportFilters.mr99_status.forEach((s) =>
				queryParams.append("mr99_status", s),
			);
			exportFilters.ppap_status.forEach((s) =>
				queryParams.append("ppap_status", s),
			);
			exportFilters.wf_index.forEach((w) => queryParams.append("wf_index", w));
			exportFilters.part_status.forEach((s) =>
				queryParams.append("part_status", s),
			);
			exportFilters.btt_status.forEach((s) =>
				queryParams.append("btt_status", s),
			);

			// Add search if present
			if (exportFilters.search) {
				queryParams.append("search", exportFilters.search);
			}
			// FET / PET year range
			if (exportFilters.fet_from) queryParams.append("fet_from", exportFilters.fet_from);
			if (exportFilters.fet_to) queryParams.append("fet_to", exportFilters.fet_to);
			if (exportFilters.pet_from) queryParams.append("pet_from", exportFilters.pet_from);
			if (exportFilters.pet_to) queryParams.append("pet_to", exportFilters.pet_to);
			// Special release filters
			(exportFilters.sofrei_erf || []).forEach((v) => queryParams.append("sofrei_erf", v));
			if (exportFilters.sofrei_ende_from) queryParams.append("sofrei_ende_from", exportFilters.sofrei_ende_from);
			if (exportFilters.sofrei_ende_to) queryParams.append("sofrei_ende_to", exportFilters.sofrei_ende_to);
			// Tags filter
			(exportFilters.tags || []).forEach((t) => queryParams.append("tags", t)); // Use apiClient for proper authentication and CORS handling.
			// Export is a GET — strip X-User-Name so API Gateway's CORS preflight passes.
			console.log("📤 Calling S3 export API via apiClient...");

			const response = await apiClient.get(
				`/export-series-csv?${queryParams.toString()}`,
				{
					headers: { "X-User-Name": undefined },
				},
			);

			// The response data should contain the export result
			const exportResult = response.data;

			if (exportResult.success && exportResult.download_url) {
				console.log("✅ Export successful:", exportResult);

				// Return the result to be handled by the modal
				return exportResult;
			} else {
				throw new Error(
					exportResult.message || "Export failed - no download URL received",
				);
			}
		} catch (error) {
			console.error("❌ Error exporting Series data:", error);

			// Handle different error types for better user experience
			if (error.response) {
				// API returned an error response
				const errorMessage =
					error.response.data?.detail ||
					error.response.data?.message ||
					"Export failed";
				throw new Error(errorMessage);
			} else if (error.request) {
				// Request was made but no response received
				throw new Error(
					"Network error - please check your connection and try again",
				);
			} else {
				// Something else happened
				throw error;
			}
		} finally {
			setIsExporting(false);
		}
	};

	// Handle opening export modal
	const handleOpenExportModal = () => {
		setIsExportModalOpen(true);
	};

	// Handle closing export modal
	const handleCloseExportModal = () => {
		setIsExportModalOpen(false);
	};

	// Handle opening reminder email modal
	const handleOpenReminderEmailModal = () => {
		if (!selectedItems || selectedItems.length === 0) {
			alert("Please select at least one record to send a reminder email.");
			return;
		}
		setIsReminderEmailModalOpen(true);
	};

	// Handle closing reminder email modal
	const handleCloseReminderEmailModal = () => {
		setIsReminderEmailModalOpen(false);
	};

	// Handle opening email history modal
	const handleOpenEmailHistoryModal = () => {
		setIsEmailHistoryModalOpen(true);
	};
	// Handle closing email history modal
	const handleCloseEmailHistoryModal = () => {
		setIsEmailHistoryModalOpen(false);
	};

	// ── Serie Bulk Edit Modal handlers ──────────────────────────────────────────

	const handleOpenSerieBulkEditModal = () => {
		if (!selectedItems || selectedItems.length === 0) {
			alert("Please select at least one record to bulk edit.");
			return;
		}
		if (selectedItems.length > 100) {
			alert(
				`Bulk edit is limited to 100 records at a time. You have selected ${selectedItems.length} records. Please deselect some items and try again.`,
			);
			return;
		}
		setSelectedItemsForSerieBulkEdit([...selectedItems]);
		setIsSerieBulkEditModalOpen(true);
	};

	const handleCloseSerieBulkEditModal = () => {
		setIsSerieBulkEditModalOpen(false);
		setSerieBulkProgress(null);
	};
	const handleSerieBulkEditSave = async (items, changes) => {
		if (!items || items.length === 0) return;

		setIsSerieBulkSaving(true);
		setSerieBulkProgress({
			current: 0,
			total: items.length,
			currentOperation: "Preparing bulk update…",
		});

		const userName = getUserDisplayName();
		const headers = { "X-User-Name": getUserEmail() };

		try {
			// Build the payload: one entry per item, with all changed fields embedded
			const updates = items.map((item) => {
				const snr = item.SNR || item.snr;
				const snr_anlage_projnr = item.project || item.snr_anlage_projnr;
				const itemUpdates = {};

				if (changes.flagging !== undefined)
					itemUpdates.flagging = changes.flagging;
				if ("status_toggle" in changes)
					itemUpdates.status_toggle = changes.status_toggle;
				if ("btt_status" in changes)
					itemUpdates.btt_status = changes.btt_status;
				if ("responsible" in changes)
					itemUpdates.responsible = changes.responsible;
				if ("followup_date" in changes)
					itemUpdates.followup_date = changes.followup_date;
				if (changes.comment) itemUpdates.comment = changes.comment;
				if ("tags" in changes) itemUpdates.tags = changes.tags;

				return { snr, snr_anlage_projnr, updates: itemUpdates };
			});

			setSerieBulkProgress({
				current: Math.round(items.length * 0.1),
				total: items.length,
				currentOperation: `Sending bulk update for ${items.length} record(s)…`,
			});

			// Single request — backend handles all fields in one Athena UPDATE + batch comment INSERT
			await apiClient.post(
				"/api/bulk-update-series-comprehensive",
				{
					updates,
					user_name: userName,
				},
				{ headers },
			);
			setSerieBulkProgress({
				current: Math.round(items.length * 0.85),
				total: items.length,
				currentOperation: "Updating rows…",
			});

			// ── Patch only the changed rows in-place (no scroll reset) ──────────────
			if (dataTableRef.current && dataTableRef.current.patchRows) {
				const patches = items.map((item) => {
					const snr = item.SNR || item.snr;
					const snr_anlage_projnr = item.project || item.snr_anlage_projnr;
					const patch = { snr, snr_anlage_projnr };

					if (changes.flagging !== undefined) patch.flagging = changes.flagging;
					if ("status_toggle" in changes)
						patch.status_toggle = changes.status_toggle;
					if ("btt_status" in changes)
						patch.btt_status = Array.isArray(changes.btt_status)
							? changes.btt_status.join(",")
							: changes.btt_status || "";
					if ("responsible" in changes)
						patch.atr_verantwortlich = changes.responsible;
					if ("followup_date" in changes)
						patch.atr_wiedervorlage = changes.followup_date;
					if (changes.comment) patch.latest_comment = changes.comment;
					if ("tags" in changes) patch.tags = changes.tags || "";

					return patch;
				});
				dataTableRef.current.patchRows(patches);
			}

			// Clear selections and close modal — scroll position is preserved
			setSelectedItems([]);
			setAllSelected(false);
			setIsSerieBulkEditModalOpen(false);
		} catch (error) {
			console.error("❌ Serie bulk edit save error:", error);
			alert(
				`Bulk edit failed: ${error?.response?.data?.detail || error.message || "Unknown error"}`,
			);
		} finally {
			setIsSerieBulkSaving(false);
			setSerieBulkProgress(null);
		}
	};
	// ── End Serie Bulk Edit handlers ─────────────────────────────────────────────

	const handleSelectItem = (item) => {
		const isSelected = selectedItems.some(
			(selected) => selected.id === item.id,
		);
		if (isSelected) {
			setSelectedItems(
				selectedItems.filter((selected) => selected.id !== item.id),
			);
		} else {
			setSelectedItems([...selectedItems, item]);
		}
	};

	const handleSelectAll = () => {
		if (allSelected) {
			setSelectedItems([]);
		}
		setAllSelected(!allSelected);
	};
	const handleFiltersChange = (filters, viewType) => {
		// Update local state for filters
		setSelectedProject(filters.project || []);
		setSelectedProgramm(filters.project || []);
		setSelectedPurchaseType(filters.purchaseType || []);
		setSelectedSeTeams(filters.seTeams || []);
		setSelectedGxClassification(filters.gxClassification || []);
		setSelectedRampUpGroup(filters.rampUpGroup || []);
		setSelectedBemiErf(filters.bemiErf || []);
		setSelectedFlag(filters.flag || []);
		setSelectedProjectNo(filters.projectNo || []);
		setSelectedAlg(filters.alg || []);
		setSelectedProgrammFilter(filters.programm || []);
		setSelectedChangeNo(filters.changeNo || []);
		setSelectedSupplier(filters.supplier || []);
		setSelectedSnr(filters.snr || []);
		setSelectedResponsible(filters.responsible || []);
		setSelectedSeTeam(filters.seTeam || []);
		setSelectedFollowupDate({
			from: filters.followupDateFrom || null,
			to: filters.followupDateTo || null,
		});
		setSelectedBza(filters.bza || []);
		setSelectedA80Status(filters.a80Status || []);
		setSelectedE99Status(filters.e99Status || []);
		setSelectedP99Status(filters.p99Status || []);
		setSelectedQi000Status(filters.qi000Status || []);
		setSelectedQi100Status(filters.qi100Status || []);
		setSelectedQi500Status(filters.qi500Status || []);
		setSelectedQi900Status(filters.qi900Status || []);
		setSelectedLr99Status(filters.lr99Status || []);
		setSelectedMr99Status(filters.mr99Status || []);
		setSelectedPpapStatus(filters.ppapStatus || []);
		setSelectedWfIndex(filters.wfIndex || []);
		setSelectedPartStatus(filters.partStatus || []);
		setSelectedPartNoPrefix(filters.partNoPrefix || []);
		setSelectedBttStatus(filters.bttStatus || []);
		setSelectedWfAlg(filters.wfAlg || []);
		setSelectedWfStufe(filters.wfStufe || []);
		setSelectedWfBza(filters.wfBza || []);
		setSelectedFetFrom(filters.fetFrom || null);
		setSelectedFetTo(filters.fetTo || null);
		setSelectedPetFrom(filters.petFrom || null);
		setSelectedPetTo(filters.petTo || null);
		setSelectedSofreiErf(filters.sofreiErf || []);
		setSelectedSofreiEndeFrom(filters.sofreiEndeFrom || null);
		setSelectedSofreiEndeTo(filters.sofreiEndeTo || null);
		setSelectedTags(filters.tags || []);
		setSelectedViewType(viewType || selectedViewType);

		// Call the fetchData method of the DataTable component
		// When applying new filters, always start from page 1
		if (dataTableRef.current && dataTableRef.current.fetchData) {
			// For Series tracking, we need to handle projectNo, alg, and changeNo filters specifically
			const seriesFilters = {
				...filters,
				programm: filters.programm || [],
				flag: filters.flag || [],
				a80Status: filters.a80Status || [],
				e99Status: filters.e99Status || [],
				p99Status: filters.p99Status || [],
				qi000Status: filters.qi000Status || [],
				qi100Status: filters.qi100Status || [],
				qi500Status: filters.qi500Status || [],
				qi900Status: filters.qi900Status || [],
				lr99Status: filters.lr99Status || [],
				mr99Status: filters.mr99Status || [],
				ppapStatus: filters.ppapStatus || [],
				wfIndex: filters.wfIndex || [],
				partStatus: filters.partStatus || [],
				partNoPrefix: filters.partNoPrefix || [],
				bttStatus: filters.bttStatus || [],
				wfAlg: filters.wfAlg || [],
				search: searchInput || "",
				sofreiErf: filters.sofreiErf || [],
				sofreiEndeFrom: filters.sofreiEndeFrom || null,
				sofreiEndeTo: filters.sofreiEndeTo || null,
				// Always re-attach pinned email kast_keys so the result set never expands beyond what was emailed
				kastKeys:
					emailKastKeys.length > 0 ? emailKastKeys : filters.kastKeys || [],
				tags: filters.tags || [],
			};

			console.log(
				"🔧 Applying filters with current search to series data table:",
				seriesFilters,
			);
			dataTableRef.current.fetchData(
				seriesFilters,
				1,
				itemsPerPage,
				viewType || selectedViewType,
			);
		}
	};
	const handleClearFilters = () => {
		// Close the "show more" tooltip immediately
		setShowFilterTooltip(false);
		// Reset all filter states
		setSelectedProject([]);
		setSelectedProgramm([]);
		setSelectedPurchaseType([]);
		setSelectedSeTeams([]);
		setSelectedGxClassification([]);
		setSelectedRampUpGroup([]);
		setSelectedBemiErf([]);
		setSelectedFlag([]);
		setSelectedProjectNo([]);
		setSelectedAlg([]);
		setSelectedProgrammFilter([]);
		setSelectedChangeNo([]);
		setSelectedSupplier([]);
		setSelectedSnr([]);
		setSelectedResponsible([]);
		setSelectedSeTeam([]);
		setSelectedFollowupDate({ from: null, to: null });
		setSelectedBza([]);
		setSelectedA80Status([]);
		setSelectedE99Status([]);
		// Release the email-pinned kast_keys lock so the user gets the full dataset
		setEmailKastKeys([]);
		setSelectedP99Status([]);
		setSelectedQi000Status([]);
		setSelectedQi100Status([]);
		setSelectedQi500Status([]);
		setSelectedQi900Status([]);
		setSelectedLr99Status([]);
		setSelectedMr99Status([]);
		setSelectedPpapStatus([]);
		setSelectedWfIndex([]);
		setSelectedPartStatus([]);
		setSelectedPartNoPrefix([]);
		setSelectedBttStatus([]);
		setSelectedWfAlg([]);
		setSelectedWfStufe([]);
		setSelectedWfBza([]);
		setSelectedFetFrom(null);
		setSelectedFetTo(null);
		setSelectedPetFrom(null);
		setSelectedPetTo(null);
		setSelectedSofreiErf([]);
		setSelectedSofreiEndeFrom(null);
		setSelectedSofreiEndeTo(null);
		setSelectedTags([]);
		setSearchInput("");
		setSearchQuery("");
		setSearchError(""); // Force SerieSidebar to remount so its internal selectedFilters state is reset too
		setSidebarResetKey((k) => k + 1);
		// Clear dashboard initial filters so the sidebar remounts with empty state
		setDashboardInitialFilters({});

		// Reset view type to Prototype to prevent race condition
		setSelectedViewType("Prototype"); // Use setTimeout to ensure state updates are processed before fetching data
		setTimeout(() => {
			if (dataTableRef.current && dataTableRef.current.fetchData) {
				// Reset sort order when clearing filters
				if (dataTableRef.current.resetSort) {
					dataTableRef.current.resetSort();
				}

				// Reset the initial data loaded flag so it can load initial data again
				if (dataTableRef.current.resetInitialDataFlag) {
					dataTableRef.current.resetInitialDataFlag();
				}
				// Explicitly pass empty filters including search to ensure complete reset
				const clearFiltersPayload = {
					projectNo: [],
					alg: [],
					changeNo: [],
					bza: [], // Add BZA filter to clear payload
					search: "", // Explicitly clear search
				};
				// Pass null as the 6th parameter to explicitly clear sort order
				dataTableRef.current.fetchData(
					clearFiltersPayload,
					1,
					itemsPerPage,
					"Prototype",
					false,
					null,
				);
			}
		}, 0);
	};

	const handleViewTypeChange = (newViewType) => {
		// If view type is actually changing
		if (newViewType !== selectedViewType) {
			// Get the current items per page from the data table if available
			const currentItemsPerPage = dataTableRef.current?.getItemsPerPage
				? dataTableRef.current.getItemsPerPage()
				: itemsPerPage;

			// Ensure we have a valid items per page value, always use 100
			const validItemsPerPage = 100;

			// Update the local state with the current items per page
			if (validItemsPerPage !== itemsPerPage) {
				setItemsPerPage(validItemsPerPage);
			}
			// Reset to first page when changing view type
			const currentFilters = {
				project: selectedProject,
				programm: selectedProgrammFilter, // Use selectedProgrammFilter for programm
				purchaseType: selectedPurchaseType,
				seTeams: selectedSeTeams,
				gxClassification: selectedGxClassification,
				rampUpGroup: selectedRampUpGroup,
				bemiErf: selectedBemiErf,
				flag: selectedFlag,
				projectNo: selectedProjectNo,
				alg: selectedAlg,
				changeNo: selectedChangeNo,
				supplier: selectedSupplier,
				snr: selectedSnr,
				responsible: selectedResponsible,
				seTeam: selectedSeTeam,
				followupDateFrom: selectedFollowupDate?.from || null,
				followupDateTo: selectedFollowupDate?.to || null,
				bza: selectedBza,
				a80Status: selectedA80Status,
				e99Status: selectedE99Status,
				p99Status: selectedP99Status,
				qi000Status: selectedQi000Status,
				qi100Status: selectedQi100Status,
				qi500Status: selectedQi500Status,
				qi900Status: selectedQi900Status,
				wfAlg: selectedWfAlg,
				wfStufe: selectedWfStufe,
				wfBza: selectedWfBza,
				lr99Status: selectedLr99Status,
				mr99Status: selectedMr99Status,
				fetFrom: selectedFetFrom,
				fetTo: selectedFetTo,
				petFrom: selectedPetFrom,
				petTo: selectedPetTo,
				ppapStatus: selectedPpapStatus,
				search: searchQuery,
				sofreiErf: selectedSofreiErf,
				sofreiEndeFrom: selectedSofreiEndeFrom,
				sofreiEndeTo: selectedSofreiEndeTo,
				kastKeys: emailKastKeys.length > 0 ? emailKastKeys : [],
			};

			// Update the view type
			setSelectedViewType(newViewType);

			// Fetch data with current filters, reset to page 1, and use the current itemsPerPage
			if (dataTableRef.current?.fetchData) {
				dataTableRef.current.fetchData(
					currentFilters,
					1,
					validItemsPerPage,
					newViewType,
				);
			}
		}
	};
	const handleClearAllFilters = async () => {
		// Close the "show more" tooltip immediately
		setShowFilterTooltip(false);

		// Reset all filter states to their initial empty values
		setSelectedProject([]);
		setSelectedPurchaseType([]);
		setSelectedProgramm([]);
		setSelectedSeTeams([]);
		setSelectedGxClassification([]);
		setSelectedRampUpGroup([]);
		setSelectedBemiErf([]);
		setSelectedFlag([]);
		setSelectedProjectNo([]);
		setSelectedAlg([]);
		setSelectedProgrammFilter([]);
		setSelectedChangeNo([]);
		setSelectedSupplier([]);
		setSelectedSnr([]);
		setSelectedResponsible([]);
		setSelectedSeTeam([]);
		setSelectedFollowupDate({ from: null, to: null });
		setSelectedBza([]);
		setSelectedA80Status([]);
		setSelectedE99Status([]);
		setSelectedP99Status([]);
		setSelectedQi000Status([]);
		setSelectedQi100Status([]);
		setSelectedQi500Status([]);
		setSelectedQi900Status([]);
		setSelectedLr99Status([]);
		setSelectedMr99Status([]);
		setSelectedPpapStatus([]);
		setSelectedWfAlg([]);
		setSelectedWfStufe([]);
		setSelectedWfBza([]);
		setSelectedPartStatus([]);
		setSelectedPartNoPrefix([]);
		setSearchInput("");
		setSearchQuery("");
		setSearchError("");
		setSelectedSofreiErf([]);
		setSelectedSofreiEndeFrom(null);
		setSelectedSofreiEndeTo(null);
		setSelectedViewType("Prototype");
		// Release the email-pinned kast_keys lock so the user gets the full dataset
		setEmailKastKeys([]);

		// Use setTimeout to ensure state updates are processed before fetching data
		setTimeout(() => {
			if (dataTableRef.current && dataTableRef.current.fetchData) {
				// Pass completely empty filters including search
				const clearFiltersPayload = {
					projectNo: [],
					alg: [],
					programm: [],
					changeNo: [],
					supplier: [],
					snr: [],
					responsible: [],
					seTeam: [],
					followupDateFrom: null,
					followupDateTo: null,
					bza: [],
					a80Status: [],
					e99Status: [],
					p99Status: [],
					qi000Status: [],
					qi100Status: [],
					qi500Status: [],

					qi900Status: [],
					lr99Status: [],
					mr99Status: [],
					ppapStatus: [],
					partStatus: [],
					search: "",
				};
				console.log("🧹 Clearing all filters and search:", clearFiltersPayload);
				dataTableRef.current.fetchData(
					clearFiltersPayload,
					1,
					itemsPerPage,
					"Prototype",
				);
			}
		}, 0);
	};
	const handleItemsPerPageChange = (newItemsPerPage) => {
		// Always use 100 items per page to prevent inconsistencies
		const standardItemsPerPage = 100;

		// Only proceed if the page size actually changed
		if (standardItemsPerPage === itemsPerPage) return;
		const currentFilters = {
			project: selectedProject,
			purchaseType: selectedPurchaseType,
			programm: selectedProgrammFilter, // Use selectedProgrammFilter for programm
			seTeams: selectedSeTeams,
			gxClassification: selectedGxClassification,
			rampUpGroup: selectedRampUpGroup,
			bemiErf: selectedBemiErf,
			flag: selectedFlag,
			projectNo: selectedProjectNo,
			alg: selectedAlg,
			changeNo: selectedChangeNo,
			supplier: selectedSupplier,
			snr: selectedSnr,
			responsible: selectedResponsible,
			seTeam: selectedSeTeam,
			followupDateFrom: selectedFollowupDate?.from || null,
			followupDateTo: selectedFollowupDate?.to || null,
			bza: selectedBza,
			a80Status: selectedA80Status,
			e99Status: selectedE99Status,
			p99Status: selectedP99Status,
			qi000Status: selectedQi000Status,
			qi100Status: selectedQi100Status,
			qi500Status: selectedQi500Status,
			qi900Status: selectedQi900Status,
			wfAlg: selectedWfAlg,
			wfStufe: selectedWfStufe,
			wfBza: selectedWfBza,
			lr99Status: selectedLr99Status,
			mr99Status: selectedMr99Status,
			fetFrom: selectedFetFrom,
			fetTo: selectedFetTo,
			petFrom: selectedPetFrom,
			petTo: selectedPetTo,
			ppapStatus: selectedPpapStatus,
			wfIndex: selectedWfIndex,
			partStatus: selectedPartStatus,
			partNoPrefix: selectedPartNoPrefix,
			search: searchQuery,
			sofreiErf: selectedSofreiErf,
			sofreiEndeFrom: selectedSofreiEndeFrom,
			sofreiEndeTo: selectedSofreiEndeTo,
		};

		// Update the items per page and fetch data in a single update
		setItemsPerPage(standardItemsPerPage);

		// Use a small timeout to ensure state is updated before making the request
		setTimeout(() => {
			if (dataTableRef.current?.fetchData) {
				dataTableRef.current.fetchData(
					currentFilters,
					1,
					standardItemsPerPage,
					selectedViewType,
				);
			}
		}, 0);
	};

	const BulkEditModal = ({ editOptions }) => {
		const [dropdownValues, setDropdownValues] = useState({
			se_teams: [],
			purchase_type_system: [],
			purchase_type: [],
			gx_classification: [],
			supplier: [],
		});

		// Load unique values when modal opens
		useEffect(() => {
			if (isBulkEditOpen && editOptions) {
				setDropdownValues({
					se_teams: editOptions.seTeams || [],
					purchase_type_system: editOptions.purchaseTypeSystems || [],
					purchase_type: editOptions.purchaseTypes || [],
					gx_classification: editOptions.gxClassifications || [],
					supplier: editOptions.suppliers || [],
				});
			}
		}, [isBulkEditOpen, editOptions]);

		// Clean up search timeout when modal closes
		useEffect(() => {
			if (!isBulkEditOpen) {
				clearTimeout(window.searchTimeout);
				setSearchQuery("");
				setSearchResults([]);
				setShowSearchResults(false);
			}
		}, [isBulkEditOpen]);

		// Update modal selected items when selectedItems changes
		useEffect(() => {
			setModalSelectedItems([...selectedItems]);
		}, [selectedItems]);
		const [modalSelectedItems, setModalSelectedItems] = useState([
			...selectedItems,
		]);
		const [showItemSelector, setShowItemSelector] = useState(false);
		const [currentPageItems, setCurrentPageItems] = useState([]);
		const [loadingItems, setLoadingItems] = useState(true);
		const [searchResults, setSearchResults] = useState([]);
		const [searchQuery, setSearchQuery] = useState("");
		const [isSearching, setIsSearching] = useState(false);
		const [showSearchResults, setShowSearchResults] = useState(false);
		const [bulkEditData, setBulkEditData] = useState({
			se_teams: "",
			purchase_type_system: "",
			purchase_type: "",
			gx_classification: "",
			supplier: "",
			total_req: "", // Add total_req to the state
			flag: "", // Add flag/status to the state
		});
		const [isSaving, setIsSaving] = useState(false);

		const handleBulkEditChange = (field, value) => {
			setBulkEditData((prev) => ({
				...prev,
				[field]: value,
			}));

			// Update all selected items immediately when dropdown value changes
			if (value !== "") {
				const updatedItems = modalSelectedItems.map((item) => ({
					...item,
					[field]: value,
				}));
				setModalSelectedItems(updatedItems);
			}
		};

		// Get current page items from the data table ref
		useEffect(() => {
			const getCurrentItems = async () => {
				if (dataTableRef.current && dataTableRef.current.getAllData) {
					try {
						setLoadingItems(true);
						const items = dataTableRef.current.getAllData() || [];
						setCurrentPageItems(items);
					} catch (error) {
						console.error("Error getting current page items:", error);
					} finally {
						setLoadingItems(false);
					}
				}
			};

			if (isBulkEditOpen) {
				getCurrentItems();
			}
		}, [isBulkEditOpen]);

		const handleAddItems = () => {
			setShowItemSelector(!showItemSelector);
		};

		// Search functionality for finding items to add
		const handleSearchItems = async (query) => {
			if (!query.trim()) {
				setSearchResults([]);
				setShowSearchResults(false);
				return;
			}

			setIsSearching(true);
			setShowSearchResults(true);

			try {
				// Use the same search endpoint as the main table
				const searchPayload = {
					project: selectedProject || [],
					programm: selectedProgrammFilter || [], // Use selectedProgrammFilter for programm
					purchase_type_system: [],
					purchase_type: selectedPurchaseType || [],
					se_teams: selectedSeTeams || [],
					gx_classification: selectedGxClassification || [],
					ramp_up_group: selectedRampUpGroup || [],
					bemi_erf: selectedBemiErf || [],
					flag: selectedFlag || [],
					supplier: [],
					search: query,
					page: 1,
					items_per_page: 50, // Limit search results
					view_type:
						selectedViewType === "Both" ? "Combined" : selectedViewType,
				};

				const response = await apiClient.post(
					"/api/filtered-data",
					searchPayload,
				);

				const result = response.data;
				setSearchResults(result.data || []);
			} catch (error) {
				console.error("Error searching items:", error);
				setSearchResults([]);
			} finally {
				setIsSearching(false);
			}
		};

		const handleSearchInputChange = (e) => {
			const query = e.target.value;
			setSearchQuery(query);

			// Debounce search
			clearTimeout(window.searchTimeout);
			window.searchTimeout = setTimeout(() => {
				handleSearchItems(query);
			}, 300);
		};

		const handleClearSearch = () => {
			setSearchQuery("");
			setSearchResults([]);
			setShowSearchResults(false);
			clearTimeout(window.searchTimeout);
		};

		const handleModalSelectItem = (item) => {
			const itemId = item.snr || item.id; // Use snr as the primary identifier if available
			const isSelected = modalSelectedItems.some(
				(selected) => (selected.snr || selected.id) === itemId,
			);
			if (isSelected) {
				setModalSelectedItems(
					modalSelectedItems.filter((sel) => (sel.snr || sel.id) !== itemId),
				);
			} else {
				// Ensure we're not duplicating items
				if (
					!modalSelectedItems.some(
						(selected) => (selected.snr || selected.id) === itemId,
					)
				) {
					setModalSelectedItems([...modalSelectedItems, item]);
				}
			}
		};

		const handleSaveWithItems = async () => {
			try {
				// Filter out empty values
				const updates = Object.fromEntries(
					Object.entries(bulkEditData).filter(([_, v]) => v !== ""),
				);

				if (Object.keys(updates).length === 0) {
					alert("Please make changes before saving");
					return;
				}

				if (modalSelectedItems.length === 0) {
					alert("No items selected for bulk edit");
					return;
				}

				// Show confirmation dialog
				// const confirmed = window.confirm(
				//   `Are you sure you want to update ${modalSelectedItems.length} item(s) with the selected changes?`
				// );

				// if (!confirmed) return;

				// Start loading state
				setIsSaving(true);

				// Prepare the bulk update request
				const bulkUpdateRequest = {
					items: modalSelectedItems.map((item) => ({
						snr: item.snr,
						ptsnr: item.ptsnr,
						project: item.project,
					})),
					updates: updates,
					view_type: selectedViewType, // Add view_type to the request
				};

				console.log("📝 Sending bulk update request:", bulkUpdateRequest);

				// Call the backend API for bulk update
				const response = await apiClient.put(
					"/api/bulk-update-ptsachnr-data",
					bulkUpdateRequest,
				);

				const result = response.data;
				console.log("✅ Bulk update response:", result);

				// Update the table data to reflect changes immediately
				if (dataTableRef.current && dataTableRef.current.updateLocalData) {
					// Update local data state in the table component
					dataTableRef.current.updateLocalData(modalSelectedItems, updates);
				}

				// Refresh the table data from server to ensure consistency
				if (dataTableRef.current && dataTableRef.current.fetchData) {
					// Call with current filters to maintain the same view
					const currentFilters = {
						project: selectedProject,
						purchaseType: selectedPurchaseType,
						programm: selectedProgrammFilter, // Use selectedProgrammFilter for programm
						seTeams: selectedSeTeams,
						gxClassification: selectedGxClassification,
						rampUpGroup: selectedRampUpGroup,
						bemiErf: selectedBemiErf,
						flag: selectedFlag,
						search: searchQuery,
					};
					await dataTableRef.current.fetchData(
						currentFilters,
						undefined,
						undefined,
						selectedViewType,
						true,
					);
				}

				// Clear selections both locally and in the table component
				setSelectedItems([]);
				setAllSelected(false);
				if (dataTableRef.current && dataTableRef.current.clearSelections) {
					dataTableRef.current.clearSelections();
				}

				// Show success message
				// setTimeout(() => {
				//   alert(`✅ Success!\n\nUpdated ${result.items_updated} item(s) with ${result.updated_fields.length} field(s):\n• ${result.updated_fields.join('\n• ')}\n\nChanges have been saved successfully.`);
				// }, 100);

				// Close modal and reset form
				setBulkEditData({
					se_teams: "",
					purchase_type_system: "",
					gx_classification: "",
					supplier: "",
					total_req: "",
					flag: "",
				});
				setModalSelectedItems([]);
				handleCloseBulkEdit();
			} catch (error) {
				console.error("Error saving bulk edit:", error);
				alert(`Failed to save changes: ${error.message || "Unknown error"}`);
			} finally {
				// End loading state
				setIsSaving(false);
			}
		};

		if (!isBulkEditOpen) return null;

		return (
			<div style={{ ...styles.modal, ...fontStyles.regular }}>
				<div
					style={{
						...styles.modalContent,
						width: "90%",
						maxWidth: "1200px",
						position: "relative",
					}}
				>
					<div style={styles.modalHeader}>
						<h2
							style={{ ...styles.modalTitle, fontSize: "18px", color: "#333" }}
						>
							Bulk Edit ({modalSelectedItems.length} items selected)
						</h2>
						<button
							onClick={handleCloseBulkEdit}
							style={{
								background: "none",
								border: "none",
								fontSize: "30px",
								cursor: "pointer",
								color: "#666",
								padding: "5px 10px",
								borderRadius: "4px",
								":hover": {
									backgroundColor: "#f0f0f0",
								},
							}}
						>
							×
						</button>
					</div>

					<div style={styles.modalBody}>
						{/* Selected Items Table */}
						<div style={{ marginBottom: "20px" }}>
							<h3
								style={{
									margin: "0 0 15px 0",
									fontSize: "14px",
									color: "#444",
									fontWeight: "500",
								}}
							>
								Selected Items ({modalSelectedItems.length})
							</h3>
							{modalSelectedItems.length > 0 ? (
								<div
									style={{
										maxHeight: "300px",
										overflowY: "auto",
										border: "1px solid #eee",
										borderRadius: "4px",
									}}
								>
									<table style={{ ...styles.table, width: "100%" }}>
										<thead>
											<tr style={{ backgroundColor: "#f5f5f5" }}>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													Part No.
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "160px",
													}}
												>
													Description
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "100px",
													}}
												>
													Project
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													SE Team
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													Purchase Type
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													Purchase Type System
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "80px",
													}}
												>
													Total Req
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "80px",
													}}
												>
													GX Classification
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "80px",
													}}
												>
													Status
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "100px",
													}}
												>
													Action
												</th>
											</tr>
										</thead>
										<tbody>
											{modalSelectedItems.map((item, index) => (
												<tr
													key={`${item.snr || item.id}-${index}`}
													style={{
														borderBottom: "1px solid #eee",
														":hover": { backgroundColor: "#f9f9f9" },
													}}
												>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.ptsnr || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.description || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.project || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.se_teams || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.purchase_type || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.purchase_type_system || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.total_req || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.gx_classification || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.flag || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														<button
															onClick={() => {
																setModalSelectedItems(
																	modalSelectedItems.filter(
																		(_, i) => i !== index,
																	),
																);
															}}
															style={{
																background: "#cc0000",
																color: "white",
																border: "none",
																borderRadius: "4px",
																padding: "4px 8px",
																cursor: "pointer",
																fontSize: "12px",
																":hover": {
																	backgroundColor: "#990000",
																},
															}}
														>
															Remove
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : (
								<div
									style={{
										padding: "20px",
										textAlign: "center",
										color: "#666",
										backgroundColor: "#f9f9f9",
										border: "1px dashed #ddd",
										borderRadius: "4px",
									}}
								>
									No items selected. Use the table to select items for bulk
									edit.
								</div>
							)}
						</div>

						{/* Search Section */}
						<div style={{ marginBottom: "20px" }}>
							<h3
								style={{
									margin: "0 0 15px 0",
									fontSize: "14px",
									color: "#444",
									fontWeight: "500",
								}}
							>
								Search & Add Items
							</h3>

							{/* Search Input */}
							<div style={{ position: "relative", marginBottom: "15px" }}>
								<input
									type="text"
									placeholder="Search for PNR, SNR, Description to add items..."
									value={searchQuery}
									onChange={handleSearchInputChange}
									style={{
										width: "100%",
										padding: "10px 40px 10px 40px",
										border: "1px solid #ddd",
										borderRadius: "4px",
										fontSize: "14px",
										boxSizing: "border-box",
										boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
									}}
								/>
								<Search
									style={{
										position: "absolute",
										left: "12px",
										top: "50%",
										transform: "translateY(-50%)",
										color: "#666",
										width: "16px",
										height: "16px",
										pointerEvents: "none",
									}}
								/>
								{searchQuery && (
									<button
										type="button"
										onClick={handleClearSearch}
										style={{
											position: "absolute",
											right: "12px",
											top: "50%",
											transform: "translateY(-50%)",
											background: "none",
											border: "none",
											color: "#999",
											cursor: "pointer",
											padding: "4px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: "18px",
											lineHeight: 1,
										}}
										aria-label="Clear search"
									>
										×
									</button>
								)}
							</div>

							{/* Search Results */}
							{showSearchResults && (
								<div
									style={{
										border: "1px solid #e0e0e0",
										borderRadius: "4px",
										overflow: "hidden",
										boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
										marginBottom: "15px",
									}}
								>
									<div
										style={{
											backgroundColor: "#f5f5f5",
											padding: "10px 15px",
											borderBottom: "1px solid #e0e0e0",
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<h4
											style={{
												margin: 0,
												fontSize: "13px",
												color: "#333",
												fontWeight: "500",
											}}
										>
											Search Results
										</h4>
										<div style={{ fontSize: "12px", color: "#666" }}>
											{isSearching
												? "Searching..."
												: `${searchResults.length} items found`}
											{searchResults.length > 0 &&
												modalSelectedItems.filter((item) =>
													searchResults.some(
														(result) =>
															(result.snr || result.id) ===
															(item.snr || item.id),
													),
												).length > 0 && (
													<span
														style={{
															marginLeft: "10px",
															color: "black",
															fontWeight: "bold",
														}}
													>
														(
														{
															modalSelectedItems.filter((item) =>
																searchResults.some(
																	(result) =>
																		(result.snr || result.id) ===
																		(item.snr || item.id),
																),
															).length
														}{" "}
														selected)
													</span>
												)}
										</div>
									</div>

									<div style={{ maxHeight: "300px", overflowY: "auto" }}>
										{isSearching ? (
											<Box display="flex" justifyContent="center" padding={4}>
												<CircularProgress size={24} sx={{ color: "#cc0000" }} />
											</Box>
										) : searchResults.length > 0 ? (
											<table
												style={{
													...styles.table,
													width: "100%",
													borderCollapse: "collapse",
												}}
											>
												<thead>
													<tr style={{ backgroundColor: "#f9f9f9" }}>
														<th
															style={{
																...styles.tableHeader,
																width: "40px",
																padding: "8px",
															}}
														></th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Part No.
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Description
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Project
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															SE Team
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Purchase Type
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															GX Classification
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
																width: "80px",
															}}
														>
															Action
														</th>
													</tr>
												</thead>
												<tbody>
													{searchResults.map((item) => {
														const isAlreadySelected = modalSelectedItems.some(
															(selected) =>
																(selected.snr || selected.id) ===
																(item.snr || item.id),
														);
														return (
															<tr
																key={item.snr || item.id}
																style={{
																	borderBottom: "1px solid #eee",
																	backgroundColor: isAlreadySelected
																		? "#f0f8ff"
																		: "transparent",
																}}
															>
																<td
																	style={{
																		...styles.tableCell,
																		textAlign: "center",
																		padding: "8px",
																	}}
																>
																	<input
																		type="checkbox"
																		checked={isAlreadySelected}
																		onChange={() => handleModalSelectItem(item)}
																		style={{ cursor: "pointer" }}
																	/>
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.ptsnr || item.snr || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																		maxWidth: "200px",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																	}}
																>
																	{item.description || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.project || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.se_teams || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.purchase_type || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.gx_classification || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																		textAlign: "center",
																	}}
																>
																	{!isAlreadySelected ? (
																		<button
																			onClick={() =>
																				handleModalSelectItem(item)
																			}
																			style={{
																				backgroundColor: "#cc0000",
																				color: "white",
																				border: "none",
																				borderRadius: "4px",
																				padding: "4px 8px",
																				cursor: "pointer",
																				fontSize: "11px",
																				":hover": {
																					backgroundColor: "#cc0000",
																				},
																			}}
																		>
																			Add
																		</button>
																	) : (
																		<span
																			style={{
																				fontSize: "11px",
																				color: "black",
																				fontWeight: "bold",
																			}}
																		>
																			✓ Added
																		</span>
																	)}
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										) : (
											<Box padding={2} textAlign="center" color="#666">
												{searchQuery
													? "No items found matching your search"
													: "Enter search terms to find items"}
											</Box>
										)}
									</div>
								</div>
							)}
						</div>

						{/* Add Items Button */}
						<div
							style={{
								marginBottom: "20px",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<h3
								style={{
									margin: 0,
									fontSize: "14px",
									color: "#444",
									fontWeight: "500",
								}}
							>
								Add More Items
							</h3>
							<button
								onClick={handleAddItems}
								style={{
									backgroundColor: "#666",
									color: "white",
									border: "none",
									padding: "8px 16px",
									cursor: "pointer",
									fontSize: "13px",
									borderRadius: "4px",
									display: "flex",
									alignItems: "center",
									gap: "6px",
									":hover": {
										backgroundColor: "#555",
									},
								}}
							>
								{showItemSelector ? (
									<>
										<ChevronDown size={16} />
										Hide Item Selector
									</>
								) : (
									<>
										<Plus size={16} />
										Add Items from Current Page
									</>
								)}
							</button>
						</div>

						{/* Item Selector */}
						{showItemSelector && (
							<div
								style={{
									marginBottom: "20px",
									border: "1px solid #e0e0e0",
									borderRadius: "4px",
									overflow: "hidden",
									boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
								}}
							>
								<div
									style={{
										backgroundColor: "#666",
										padding: "10px 15px",
										borderBottom: "1px solid #e0e0e0",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<h4
										style={{
											margin: 0,
											fontSize: "13px",
											color: "white",
											fontWeight: "500",
										}}
									>
										Select Items from Current Page
									</h4>
									<div style={{ fontSize: "12px", color: "white" }}>
										{currentPageItems.length} items available
									</div>
								</div>

								<div style={{ maxHeight: "300px", overflowY: "auto" }}>
									{loadingItems ? (
										<Box display="flex" justifyContent="center" padding={4}>
											<CircularProgress size={24} sx={{ color: "#cc0000" }} />
										</Box>
									) : currentPageItems.length > 0 ? (
										<table
											style={{
												...styles.table,
												width: "100%",
												borderCollapse: "collapse",
											}}
										>
											<thead>
												<tr style={{ backgroundColor: "#f5f5f5" }}>
													<th
														style={{
															...styles.tableHeader,
															width: "40px",
															padding: "8px",
														}}
													></th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														Part no.
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														Description
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														Project
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														SE Team
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														Purchase Type
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														Purchase Type System
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														Total Req
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														GX Classification
													</th>
													<th
														style={{
															...styles.tableHeader,
															padding: "8px",
															textAlign: "left",
														}}
													>
														Status
													</th>
												</tr>
											</thead>
											<tbody>
												{currentPageItems.map((item) => (
													<tr key={item.snr || item.id}>
														<td
															style={{
																...styles.tableCell,
																textAlign: "center",
															}}
														>
															<input
																type="checkbox"
																checked={modalSelectedItems.some(
																	(selected) =>
																		selected.snr === (item.snr || item.id),
																)}
																onChange={() => handleModalSelectItem(item)}
																style={{ cursor: "pointer" }}
															/>
														</td>
														<td
															style={{ ...styles.tableCell, fontSize: "12px" }}
														>
															{item.ptsnr || item.snr || "N/A"}
														</td>
														<td
															style={{ ...styles.tableCell, fontSize: "12px" }}
														>
															{item.description || "N/A"}
														</td>
														<td
															style={{ ...styles.tableCell, fontSize: "12px" }}
														>
															{item.project || "N/A"}
														</td>
														<td
															style={{ ...styles.tableCell, fontSize: "12px" }}
														>
															{item.se_teams || "N/A"}
														</td>
														<td
															style={{
																...styles.tableCell,
																padding: "10px",
																fontSize: "12px",
															}}
														>
															{item.purchase_type || "N/A"}
														</td>
														<td
															style={{
																...styles.tableCell,
																padding: "10px",
																fontSize: "12px",
															}}
														>
															{item.purchase_type_system || "N/A"}
														</td>
														<td
															style={{
																...styles.tableCell,
																padding: "10px",
																fontSize: "12px",
															}}
														>
															{item.total_req || "N/A"}
														</td>
														<td
															style={{
																...styles.tableCell,
																padding: "10px",
																fontSize: "12px",
															}}
														>
															{item.gx_classification || "N/A"}
														</td>
														<td
															style={{
																...styles.tableCell,
																padding: "10px",
																fontSize: "12px",
															}}
														>
															{item.flag || "N/A"}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									) : (
										<Box padding={2} textAlign="center" color="#666">
											No items found on the current page
										</Box>
									)}
								</div>
							</div>
						)}

						<div style={{ marginBottom: "20px" }}>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: "15px",
								}}
							>
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										SET Allocation
									</label>
									<select
										style={styles.select}
										value={bulkEditData.se_teams}
										onChange={(e) =>
											handleBulkEditChange("se_teams", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										{dropdownValues.se_teams.map((team) => (
											<option key={team} value={team}>
												{team}
											</option>
										))}
									</select>
								</div>

								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Purchase Type
									</label>
									<select
										style={styles.select}
										value={bulkEditData.purchase_type}
										onChange={(e) =>
											handleBulkEditChange("purchase_type", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										{dropdownValues.purchase_type.map((type) => (
											<option key={type} value={type}>
												{type}
											</option>
										))}
									</select>
								</div>

								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Gx Classification
									</label>
									<select
										style={styles.select}
										value={bulkEditData.gx_classification}
										onChange={(e) =>
											handleBulkEditChange("gx_classification", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										{dropdownValues.gx_classification.map((gx) => (
											<option key={gx} value={gx}>
												{gx}
											</option>
										))}
									</select>
								</div>

								{/* Total Req Field */}
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Total Req
									</label>
									<input
										type="number"
										style={{
											...styles.select,
											appearance: "none",
											WebkitAppearance: "none",
											MozAppearance: "textfield", // removes arrows in Firefox
											padding: "8px",
											fontSize: "14px",
											borderRadius: "4px",
											backgroundColor: "#fff",
											color: "#333",
											borderRadius: "0px",
											width: "97%",
											height: "18px",
										}}
										value={bulkEditData.total_req}
										onChange={(e) =>
											handleBulkEditChange("total_req", e.target.value)
										}
										placeholder="Keep existing"
									/>
								</div>

								{/* Supplier Field */}
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Supplier
									</label>
									<select
										style={styles.select}
										value={bulkEditData.supplier}
										onChange={(e) =>
											handleBulkEditChange("supplier", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										{dropdownValues.supplier.map((supplier) => (
											<option key={supplier} value={supplier}>
												{supplier}
											</option>
										))}
									</select>
								</div>

								{/* Status Field */}
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Status
									</label>
									<select
										style={styles.select}
										value={bulkEditData.flag}
										onChange={(e) =>
											handleBulkEditChange("flag", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										<option value="Y">Done (Y)</option>
										<option value="N">Not Done (N)</option>
									</select>
								</div>
							</div>
						</div>
					</div>
					<div style={styles.modalFooter}>
						<button
							onClick={handleCloseBulkEdit}
							style={{
								...styles.button,
								backgroundColor: "#fff",
								color: "#666",
								border: "1px solid #ddd",
							}}
							disabled={isSaving}
						>
							Cancel
						</button>
						<button
							onClick={handleSaveWithItems}
							style={{
								...styles.button,
								backgroundColor: "#cc0000",
								opacity: isSaving ? 0.7 : 1,
							}}
							disabled={isSaving}
						>
							{isSaving ? "Saving..." : "Save bulk edit"}
						</button>
					</div>

					{/* Loading Overlay */}
					{isSaving && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: "rgba(255, 255, 255, 0.9)",
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								zIndex: 1001,
								borderRadius: "8px",
							}}
						>
							<div
								style={{
									width: "40px",
									height: "40px",
									border: "4px solid #f3f3f3",
									borderTop: "4px solid #cc0000",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									marginBottom: "20px",
								}}
							></div>
							<div
								style={{
									fontSize: "16px",
									fontWeight: "500",
									color: "#333",
									marginBottom: "8px",
								}}
							>
								Updating {modalSelectedItems.length} item(s)...
							</div>
							<div
								style={{
									fontSize: "14px",
									color: "#666",
								}}
							>
								Please wait while we save your changes
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};
	return (
		<div style={styles.mainContainer}>
			{/* Add CSS for spinner animation */}
			<style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>{" "}
			{/* Dashboard filter loading overlay */}
			{isDashboardFilterLoading && (
				<div
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
						flexDirection: "column",
					}}
				>
					<CircularProgress
						size={80}
						sx={{ color: "#1e2832", marginBottom: 2 }}
					/>
					<Typography
						variant="h6"
						sx={{
							color: "white",
							fontFamily: "MAN Europe_Bold",
							textAlign: "center",
							maxWidth: "300px",
						}}
					>
						Loading Series Tracking Data...
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "rgba(255, 255, 255, 0.8)",
							fontFamily: "MAN Europe_Regular",
							textAlign: "center",
							marginTop: 1,
						}}
					>
						Please wait while we load your parts data
					</Typography>
				</div>
			)}{" "}
			<Header user={user} signOut={signOut} onStartTour={handleStartTour} />
			{/* Email-reminder pinned-records banner */}
			{emailKastKeys.length > 0 && (
				<div
					style={{
						position: "fixed",
						top: "74px",
						left: 0,
						right: 0,
						zIndex: 1200,
						backgroundColor: "#fff3cd",
						borderBottom: "2px solid #ffc107",
						padding: "8px 20px",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
						fontSize: "13px",
						color: "#856404",
					}}
				>
					{" "}
					<span
						style={{
							display: "flex",
							alignItems: "center",
							gap: "8px",
							fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
						}}
					>
						<img
							src="/mail.png"
							alt="Mail"
							style={{
								width: "18px",
								height: "18px",
								objectFit: "contain",
								flexShrink: 0,
							}}
						/>
						Email reminder view — showing {emailKastKeys.length} pinned record
						{emailKastKeys.length !== 1 ? "s" : ""} from the email link. Filters
						you apply will narrow down these records only.
					</span>
					<button
						onClick={() => {
							setEmailKastKeys([]);
							if (dataTableRef.current?.fetchData) {
								dataTableRef.current.fetchData(
									{ projectNo: [], alg: [], search: "" },
									1,
									itemsPerPage,
									selectedViewType,
								);
							}
						}}
						style={{
							background: "none",
							border: "1px solid #856404",
							borderRadius: "4px",
							padding: "3px 12px",
							cursor: "pointer",
							fontSize: "12px",
							color: "#856404",
							fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
							marginLeft: "16px",
							whiteSpace: "nowrap",
						}}
					>
						✕ Exit pinned view
					</button>
				</div>
			)}
			<div
				style={{
					...styles.contentWrapper,
					paddingTop: emailKastKeys.length > 0 ? "38px" : undefined,
				}}
			>
				<SerieSidebar
					key={sidebarResetKey}
					onFiltersChange={handleFiltersChange}
					onClearFilters={handleClearFilters}
					onColumnVisibilityChange={handleColumnVisibilityChange}
					columnVisibility={columnVisibility}
					selectedViewType={selectedViewType}
					onViewTypeChange={handleViewTypeChange}
					isLoading={isFilterOptionsLoading}
					error={filterOptionsError}
					initialFilters={dashboardInitialFilters}
					filterOptions={{
						projectNumbers: filterOptions.projectNumbers || [],
						algValues: filterOptions.algValues || [],
						programmValues: filterOptions.programmValues || [],
						changeNumbers: filterOptions.changeNumbers || [],
						snrValues: filterOptions.snrValues || [],
						supplierValues: filterOptions.supplierValues || [],
						responsibleValues: filterOptions.responsibleValues || [],
						seTeamValues: filterOptions.seTeamValues || [],
						followupDateValues: filterOptions.followupDateValues || [],
						bzaValues: filterOptions.bzaValues || [],
						a80StatusValues: filterOptions.a80StatusValues || [],
						e99StatusValues: filterOptions.e99StatusValues || [],
						p99StatusValues: filterOptions.p99StatusValues || [],
						qi000StatusValues: filterOptions.qi000StatusValues || [],
						qi100StatusValues: filterOptions.qi100StatusValues || [],
						qi500StatusValues: filterOptions.qi500StatusValues || [],
						qi900StatusValues: filterOptions.qi900StatusValues || [],
						lr99StatusValues: filterOptions.lr99StatusValues || [],
						mr99StatusValues: filterOptions.mr99StatusValues || [],
						ppapStatusValues: filterOptions.ppapStatusValues || [],
						wfIndexValues: filterOptions.wfIndexValues || [],
						statusToggleValues: filterOptions.statusToggleValues || [],
						wfAlgValues: filterOptions.wfAlgValues || [],
						wfStufeValues: filterOptions.wfStufeValues || [],
						wfBzaValues: filterOptions.wfBzaValues || [],
						projectAlgMapping: filterOptions.projectAlgMapping || {},
						projectChangeMapping: filterOptions.projectChangeMapping || {},
						programmAlgMapping: filterOptions.programmAlgMapping || {},
						programmChangeMapping: filterOptions.programmChangeMapping || {},
						projectSeTeamMapping: filterOptions.projectSeTeamMapping || {},
						programmSeTeamMapping: filterOptions.programmSeTeamMapping || {},
						projectBzaMapping: filterOptions.projectBzaMapping || {},
						programmBzaMapping: filterOptions.programmBzaMapping || {},
						projectPpapMapping: filterOptions.projectPpapMapping || {},
						programmPpapMapping: filterOptions.programmPpapMapping || {},
						projectWfIndexMapping: filterOptions.projectWfIndexMapping || {},
						programmWfIndexMapping: filterOptions.programmWfIndexMapping || {},
						projectSupplierMapping: filterOptions.projectSupplierMapping || {},
						programmSupplierMapping:
							filterOptions.programmSupplierMapping || {},
						projectResponsibleMapping:
							filterOptions.projectResponsibleMapping || {},
						programmResponsibleMapping:
							filterOptions.programmResponsibleMapping || {},
						sofreiErfValues: filterOptions.sofreiErfValues || [],
						tagsValues: filterOptions.tagsValues || [],
					}}
				/>
				<div style={styles.mainContent}>
					<div style={styles.searchContainer}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								marginBottom: "-15px",
								marginTop: "-5px",
								padding: "0 40px",
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									width: "100%",
								}}
							>
								<div
									style={{ display: "flex", alignItems: "center", gap: "10px" }}
								>
									<Home size={20} color="#666" />
									<span
										style={{ color: "#666", fontFamily: "MAN Europe_Bold" }}
									>
										Home
									</span>
									<ChevronRight size={16} color="#666" />
									<span style={{ fontFamily: "MAN Europe_Bold" }}>
										Series tracking
									</span>
								</div>{" "}
								<div style={{ display: "flex", gap: "10px" }}>
									<div
										style={{
											display: "flex",
											gap: "10px",
											alignItems: "center",
										}}
									>
										{/* Edit Mode Buttons */}
										{!isEditMode ? (
											<CraftButton
												purpose="standard"
												variant="secondary"
												size="s"
												type="button"
												icon-name="edit"
												onClick={handleEnableEdit}
											>
												Edit
											</CraftButton>
										) : (
											<div
												style={{
													display: "flex",
													gap: "8px",
													alignItems: "center",
												}}
											>
												<CraftButton
													purpose="standard"
													variant="secondary"
													size="s"
													type="button"
													icon-name="check"
													disabled={isSaving}
													loading={isSaving}
													onClick={handleSaveChanges}
												>
													{isSaving ? "Saving..." : "Save"}
												</CraftButton>
												<CraftButton
													purpose="standard"
													variant="primary"
													size="s"
													type="button"
													icon-name="x"
													disabled={isSaving}
													onClick={handleCancelEdit}
												>
													Cancel
												</CraftButton>
											</div>
										)}

										{/* Serie Bulk Edit button */}
										<CraftButton
											purpose="standard"
											variant="primary"
											size="s"
											type="button"
											icon-name="edit"
											disabled={
												!selectedItems ||
												selectedItems.length < 1 ||
												selectedItems.length > 100
											}
											title={
												!selectedItems || selectedItems.length < 1
													? "Select at least 1 item to bulk edit"
													: selectedItems.length > 100
														? `Bulk edit is limited to 100 records. You have selected ${selectedItems.length}.`
														: `Bulk edit ${selectedItems.length} selected items`
											}
											onClick={handleOpenSerieBulkEditModal}
											className="bulk-edit-button"
										>
											Bulk Edit
											{selectedItems && selectedItems.length > 0
												? ` (${selectedItems.length}${selectedItems.length > 100 ? " — max 100" : ""})`
												: ""}
										</CraftButton>

										{/* Actions Dropdown Menu */}
										<ActionsDropdownMenu
											selectedItems={selectedItems}
											onOpenReminderEmailModal={handleOpenReminderEmailModal}
											onOpenEmailHistoryModal={handleOpenEmailHistoryModal}
											onExportCSV={handleOpenExportModal}
										/>

										{/* Dashboard Navigation Button */}
										<CraftButton
											purpose="standard"
											variant="primary"
											size="s"
											type="button"
											icon-name="chevron-right"
											onClick={navigateToDashboard}
										>
											Dashboard
										</CraftButton>
									</div>
								</div>
							</div>
						</div>
						{/* Active Filters Display - Positioned exactly below Export and Bulk Edit buttons, right-aligned */}{" "}
						<div
							style={{
								padding: "0px 40px 0px 20px",
								position: "relative",
								top: "25px",
								backgroundColor: "#f1f1f1",
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "flex-end",
									width: "100%",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "10px",
										flexWrap: "wrap",
										position: "relative",
									}}
								>
									{" "}
									<span
										style={{
											fontSize: "14px",
											fontWeight: "bold",
											color: "#666",
											fontFamily: "MAN EUROPE_Bold",
										}}
									>
										Active Filters:
									</span>{" "}
									{/* Dashboard click-through filters now populate the sidebar state directly —
                      active filter chips appear in the standard filter display below. */}
									{(() => {
											const activeFilters = [];

											// ...existing code to populate activeFilters...
											if (selectedProject && selectedProject.length > 0) {
												activeFilters.push({
													label: "Project",
													values: selectedProject,
												});
											}
											if (selectedProgramm && selectedProgramm.length > 0) {
												activeFilters.push({
													label: "Project Name",
													values: selectedProgramm,
												});
											}
											if (
												selectedPurchaseType &&
												selectedPurchaseType.length > 0
											) {
												activeFilters.push({
													label: "Purchase Type",
													values: selectedPurchaseType,
												});
											}
											if (selectedSeTeams && selectedSeTeams.length > 0) {
												activeFilters.push({
													label: "SE-Teams",
													values: selectedSeTeams,
												});
											}
											if (
												selectedGxClassification &&
												selectedGxClassification.length > 0
											) {
												activeFilters.push({
													label: "Gx Classification",
													values: selectedGxClassification,
												});
											}
											if (
												selectedRampUpGroup &&
												selectedRampUpGroup.length > 0
											) {
												activeFilters.push({
													label: "Ramp-up Group",
													values: selectedRampUpGroup,
												});
											}
											if (selectedFlag && selectedFlag.length > 0) {
												activeFilters.push({
													label: "Flag",
													values: selectedFlag,
												});
											}
											if (selectedProjectNo && selectedProjectNo.length > 0) {
												activeFilters.push({
													label: "Project No",
													values: selectedProjectNo,
												});
											}
											if (selectedAlg && selectedAlg.length > 0) {
												activeFilters.push({
													label: "ALG",
													values: selectedAlg,
												});
											}
											if (
												selectedProgrammFilter &&
												selectedProgrammFilter.length > 0
											) {
												activeFilters.push({
													label: "Programm",
													values: selectedProgrammFilter,
												});
											}
											if (selectedChangeNo && selectedChangeNo.length > 0) {
												activeFilters.push({
													label: "Change No",
													values: selectedChangeNo,
												});
											}
											if (selectedSupplier && selectedSupplier.length > 0) {
												activeFilters.push({
													label: "Supplier",
													values: selectedSupplier,
												});
											}
											if (selectedSnr && selectedSnr.length > 0) {
												activeFilters.push({
													label: "SNR",
													values: selectedSnr,
												});
											}
											if (
												selectedResponsible &&
												selectedResponsible.length > 0
											) {
												activeFilters.push({
													label: "Responsible",
													values: selectedResponsible,
												});
											}
											if (selectedSeTeam && selectedSeTeam.length > 0) {
												activeFilters.push({
													label: "SE Team",
													values: selectedSeTeam,
												});
											}
											if (
												selectedFollowupDate?.from ||
												selectedFollowupDate?.to
											) {
												const fromStr = selectedFollowupDate.from
													? new Date(
															selectedFollowupDate.from,
														).toLocaleDateString("en-GB")
													: "Any";
												const toStr = selectedFollowupDate.to
													? new Date(
															selectedFollowupDate.to,
														).toLocaleDateString("en-GB")
													: "Any";
												activeFilters.push({
													label: "Follow-up Date",
													values: [`${fromStr} – ${toStr}`],
												});
											}
											if (selectedBza && selectedBza.length > 0) {
												activeFilters.push({
													label: "BZA",
													values: selectedBza,
												});
											}
											if (selectedA80Status && selectedA80Status.length > 0) {
												activeFilters.push({
													label: "A80 Status",
													values: selectedA80Status,
												});
											}
											if (selectedE99Status && selectedE99Status.length > 0) {
												activeFilters.push({
													label: "E99 Status",
													values: selectedE99Status,
												});
											}
											if (selectedP99Status && selectedP99Status.length > 0) {
												activeFilters.push({
													label: "P99 Status",
													values: selectedP99Status,
												});
											}
											if (
												selectedQi000Status &&
												selectedQi000Status.length > 0
											) {
												activeFilters.push({
													label: "QI000 Status",
													values: selectedQi000Status,
												});
											}
											if (
												selectedQi100Status &&
												selectedQi100Status.length > 0
											) {
												activeFilters.push({
													label: "QI100 Status",
													values: selectedQi100Status,
												});
											}
											if (
												selectedQi500Status &&
												selectedQi500Status.length > 0
											) {
												activeFilters.push({
													label: "QI500 Status",
													values: selectedQi500Status,
												});
											}
											if (
												selectedQi900Status &&
												selectedQi900Status.length > 0
											) {
												activeFilters.push({
													label: "QI900 Status",
													values: selectedQi900Status,
												});
											}
											if (selectedLr99Status && selectedLr99Status.length > 0) {
												activeFilters.push({
													label: "LR99 Status",
													values: selectedLr99Status,
												});
											}
											if (selectedMr99Status && selectedMr99Status.length > 0) {
												activeFilters.push({
													label: "MR99 Status",
													values: selectedMr99Status,
												});
											}
											if (selectedWfAlg && selectedWfAlg.length > 0) {
												activeFilters.push({
													label: "WF ALG",
													values: selectedWfAlg,
												});
											}
											if (selectedWfStufe && selectedWfStufe.length > 0) {
												activeFilters.push({
													label: "WF Stufe",
													values: selectedWfStufe,
												});
											}
											if (selectedWfBza && selectedWfBza.length > 0) {
												activeFilters.push({
													label: "WF BZA",
													values: selectedWfBza,
												});
											}
											if (selectedFetFrom || selectedFetTo) {
												const fetLabel =
													selectedFetFrom && selectedFetTo
														? `${selectedFetFrom} – ${selectedFetTo}`
														: selectedFetFrom
															? `≥ ${selectedFetFrom}`
															: `≤ ${selectedFetTo}`;
												activeFilters.push({
													label: "FET",
													values: [fetLabel],
												});
											}
											if (selectedPetFrom || selectedPetTo) {
												const petLabel =
													selectedPetFrom && selectedPetTo
														? `${selectedPetFrom} – ${selectedPetTo}`
														: selectedPetFrom
															? `≥ ${selectedPetFrom}`
															: `≤ ${selectedPetTo}`;
												activeFilters.push({
													label: "PET",
													values: [petLabel],
												});
											}
											if (selectedSofreiErf && selectedSofreiErf.length > 0) {
												activeFilters.push({
													label: "Special Release Needed",
													values: selectedSofreiErf,
												});
											}
											if (selectedSofreiEndeFrom || selectedSofreiEndeTo) {
												const sofreiEndeLabel =
													selectedSofreiEndeFrom && selectedSofreiEndeTo
														? `${selectedSofreiEndeFrom} – ${selectedSofreiEndeTo}`
														: selectedSofreiEndeFrom
															? `≥ ${selectedSofreiEndeFrom}`
															: `≤ ${selectedSofreiEndeTo}`;
												activeFilters.push({
													label: "Special Release Until",
													values: [sofreiEndeLabel],
												});
											}
											if (selectedPpapStatus && selectedPpapStatus.length > 0) {
												activeFilters.push({
													label: "PPAP Status",
													values: selectedPpapStatus,
												});
											}
											if (selectedWfIndex && selectedWfIndex.length > 0) {
												activeFilters.push({
													label: "Index Type",
													values: selectedWfIndex,
												});
											}
											if (selectedPartStatus && selectedPartStatus.length > 0) {
												activeFilters.push({
													label: "Part Status",
													values: selectedPartStatus,
												});
											}
											if (
												selectedPartNoPrefix &&
												selectedPartNoPrefix.length > 0
											) {
												activeFilters.push({
													label: "Part No Prefix",
													values: selectedPartNoPrefix,
												});
											}
											if (selectedBttStatus && selectedBttStatus.length > 0) {
												activeFilters.push({
													label: "BTT Status",
													values: selectedBttStatus,
												});
											}
											if (selectedTags && selectedTags.length > 0) {
												activeFilters.push({
													label: "Tags",
													values: selectedTags,
												});
											}
											if (searchQuery && searchQuery.trim()) {
												activeFilters.push({
													label: "Search",
													values: [searchQuery],
												});
											}

											const totalActiveFilters = activeFilters.reduce(
												(sum, filter) => sum + filter.values.length,
												0,
											);

											// Only show first filter group, and limit to 3 values
											const displayFilter =
												activeFilters.length > 0 ? activeFilters[0] : null;
											const hiddenFiltersCount = activeFilters
												.slice(1)
												.reduce((sum, filter) => sum + filter.values.length, 0);
											const hiddenValuesInFirstFilter =
												displayFilter && displayFilter.values.length > 3
													? displayFilter.values.length - 3
													: 0;
											const totalHiddenCount =
												hiddenFiltersCount + hiddenValuesInFirstFilter;

											return activeFilters.length > 0 ? (
												<>
													<span
														style={{
															fontSize: "12px",
															color: "#999",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														({totalActiveFilters} total)
													</span>
													<div
														style={{
															display: "flex",
															gap: "8px",
															flexWrap: "wrap",
															alignItems: "center",
														}}
													>
														{displayFilter &&
															displayFilter.values.length <= 3 && (
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "4px",
																	}}
																>
																	<span
																		style={{
																			fontSize: "11px",
																			fontWeight: "bold",
																			color: "#cc0000",
																			fontFamily: "MAN EUROPE_Regular",
																		}}
																	>
																		{displayFilter.label}:
																	</span>
																	<div
																		style={{
																			display: "flex",
																			gap: "2px",
																			flexWrap: "wrap",
																		}}
																	>
																		{displayFilter.values.map(
																			(value, valueIndex) => (
																				<span
																					key={valueIndex}
																					style={{
																						display: "inline-block",
																						backgroundColor: "#cc0000",
																						color: "white",
																						padding: "2px 6px",
																						borderRadius: "10px",
																						fontSize: "10px",
																						fontFamily: "MAN EUROPE_Regular",
																					}}
																				>
																					{value}
																				</span>
																			),
																		)}
																	</div>
																</div>
															)}{" "}
														{totalHiddenCount > 0 && (
															<button
																onClick={() =>
																	setShowFilterTooltip(!showFilterTooltip)
																}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																	gap: "4px",
																	backgroundColor: "#666",
																	color: "white",
																	padding: "4px 10px",
																	borderRadius: "12px",
																	fontSize: "11px",
																	fontFamily: "MAN EUROPE_Regular",
																	cursor: "pointer",
																	border: "none",
																	transition: "background-color 0.2s",
																	whiteSpace: "nowrap",
																}}
																onMouseEnter={(e) =>
																	(e.target.style.backgroundColor = "#555")
																}
																onMouseLeave={(e) =>
																	(e.target.style.backgroundColor = "#666")
																}
															>
																Show more filters ({totalHiddenCount})
															</button>
														)}
													</div>

													{/* Tooltip with close button */}
													{showFilterTooltip && (
														<div
															style={{
																position: "absolute",
																top: "100%",
																right: "0",
																marginTop: "8px",
																backgroundColor: "white",
																border: "1px solid #ddd",
																borderRadius: "8px",
																padding: "12px",
																boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
																zIndex: 1000,
																minWidth: "300px",
																maxWidth: "500px",
																maxHeight: "400px",
																overflowY: "auto",
															}}
														>
															<div
																style={{
																	display: "flex",
																	justifyContent: "space-between",
																	alignItems: "center",
																	marginBottom: "8px",
																}}
															>
																<div
																	style={{
																		fontSize: "12px",
																		fontWeight: "bold",
																		color: "#333",
																		fontFamily: "MAN EUROPE_Bold",
																	}}
																>
																	All Active Filters
																</div>
																<button
																	onClick={() => setShowFilterTooltip(false)}
																	style={{
																		background: "none",
																		border: "none",
																		fontSize: "20px",
																		cursor: "pointer",
																		color: "#666",
																		padding: "0",
																		lineHeight: "1",
																		width: "20px",
																		height: "20px",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																	}}
																	aria-label="Close"
																>
																	×
																</button>
															</div>
															{activeFilters.map((filter, index) => (
																<div
																	key={index}
																	style={{
																		marginBottom: "8px",
																		paddingBottom: "8px",
																		borderBottom:
																			index < activeFilters.length - 1
																				? "1px solid #eee"
																				: "none",
																	}}
																>
																	<div
																		style={{
																			fontSize: "11px",
																			fontWeight: "bold",
																			color: "#cc0000",
																			marginBottom: "4px",
																			fontFamily: "MAN EUROPE_Bold",
																		}}
																	>
																		{filter.label}:
																	</div>
																	<div
																		style={{
																			display: "flex",
																			gap: "4px",
																			flexWrap: "wrap",
																		}}
																	>
																		{filter.values.map((value, valueIndex) => (
																			<span
																				key={valueIndex}
																				style={{
																					display: "inline-block",
																					backgroundColor: "#cc0000",
																					color: "white",
																					padding: "2px 6px",
																					borderRadius: "10px",
																					fontSize: "10px",
																					fontFamily: "MAN EUROPE_Regular",
																				}}
																			>
																				{value}
																			</span>
																		))}
																	</div>
																</div>
															))}
														</div>
													)}
												</>
											) : (
												<span
													style={{
														fontSize: "12px",
														color: "#999",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													No active filters
												</span>
											);
										})()}
								</div>
							</div>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								marginBottom: "5px",
								padding: "0 40px",
							}}
							className="search-container"
						>
							<form
								onSubmit={handleSearchSubmit}
								style={{
									flex: 1,
									position: "relative",
									maxWidth: "600px",
									display: "flex",
									flexDirection: "column",
									gap: "8px",
								}}
							>
								<div style={{ display: "flex", gap: "8px" }}>
									<div style={{ position: "relative", flex: 1 }}>
										{" "}
										<input
											type="text"
											data-tour-target="serie-search-bar"
											placeholder="Search for SNR, Description"
											value={searchInput}
											onChange={(e) => {
												setSearchInput(e.target.value);
												// Clear error when user starts typing
												if (searchError) setSearchError("");
											}}
											onKeyDown={(e) =>
												e.key === "Enter" && handleSearchSubmit(e)
											}
											className="search-input"
											style={{
												width: "100%",
												padding: "10px 40px 10px 40px",
												border: `1px solid ${searchError ? "#ff4d4f" : "#ddd"}`,
												borderRadius: "4px",
												fontSize: "12px",
												boxSizing: "border-box",
												boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
											}}
										/>
										<Search
											style={{
												position: "absolute",
												left: "12px",
												top: "50%",
												transform: "translateY(-50%)",
												color: searchError ? "#ff4d4f" : "#666",
												width: "16px",
												height: "16px",
												pointerEvents: "none",
											}}
										/>
										{searchInput && (
											<button
												type="button"
												onClick={() => {
													setSearchInput("");
													setSearchError("");
													handleSearch("");
												}}
												style={{
													position: "absolute",
													right: "12px",
													top: "50%",
													transform: "translateY(-50%)",
													background: "none",
													border: "none",
													color: searchError ? "#ff4d4f" : "#999",
													cursor: "pointer",
													padding: "4px",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: "18px",
													lineHeight: 1,
												}}
												aria-label="Clear search"
											>
												×
											</button>
										)}
									</div>
									<button
										type="submit"
										style={{
											padding: "10px 20px",
											backgroundColor: "#1e2832",
											color: "white",
											border: "none",
											borderRadius: "0px",
											fontFamily: "MAN EUROPE_Bold",
											cursor: "pointer",
											fontSize: "14px",
											fontWeight: "bold",
											whiteSpace: "nowrap",
											transition: "background-color 0.2s",
											":hover": {
												backgroundColor: "#b30000",
											},
											height: "35px",
											alignSelf: "flex-start",
										}}
									>
										Search
									</button>
								</div>
								{searchError && (
									<div
										style={{
											color: "#ff4d4f",
											fontSize: "12px",
											marginTop: "-4px",
											paddingLeft: "8px",
										}}
									>
										{searchError}
									</div>
								)}
							</form>
						</div>
					</div>{" "}
					<div
						style={styles.tableContainer}
						className="table-container data-table"
					>
						{" "}
						<SerieTrackingDataTable
							ref={dataTableRef}
							onSelectItems={handleSelectItems}
							selectedItems={selectedItems}
							editOptions={editOptions}
							columnVisibility={columnVisibility}
							selectedViewType={selectedViewType} // Pass selectedViewType to DataTable
							onItemsPerPageChange={handleItemsPerPageChange} // Pass the new handler
							getUserDisplayName={getUserDisplayName} // Pass user display name function
							getUserEmail={getUserEmail} // Pass user email function for authorization
							skipInitialFetch={isFromDashboard()} // When navigating from dashboard, suppress the unfiltered mount-fetch
						/>
					</div>
				</div>{" "}
			</div>{" "}
			{/* Bulk Edit Modal */}
			<BulkEditModal editOptions={editOptions} />
			{/* Export Modal — background email-based export */}{" "}
			<SerieExportModal
				isOpen={isExportModalOpen}
				onClose={handleCloseExportModal}
				userEmail={getUserEmail()}
				currentFilters={{
					project_number: selectedProjectNo,
					alg: selectedAlg,
					change_no: selectedChangeNo,
					snr: selectedSnr,
					programm: selectedProgrammFilter,
					supplier: selectedSupplier,
					responsible: selectedResponsible,
					se_team: selectedSeTeam,
					followup_date_from: selectedFollowupDate?.from || null,
					followup_date_to: selectedFollowupDate?.to || null,
					bza: selectedBza,
					snr_prefix: selectedPartNoPrefix,
					a80_status: selectedA80Status,
					e99_status: selectedE99Status,
					p99_status: selectedP99Status,
					qi000_status: selectedQi000Status,
					qi100_status: selectedQi100Status,
					qi500_status: selectedQi500Status,
					qi900_status: selectedQi900Status,
					lr99_status: selectedLr99Status,
					mr99_status: selectedMr99Status,
					ppap_status: selectedPpapStatus,
					wf_index: selectedWfIndex,
					wf_alg: selectedWfAlg,
					wf_stufe: selectedWfStufe,
					wf_bza: selectedWfBza,
					btt_status: selectedBttStatus,
					part_status: selectedPartStatus,
					search: searchQuery || null,
					fet_from: selectedFetFrom || null,
					fet_to: selectedFetTo || null,
					pet_from: selectedPetFrom || null,
					pet_to: selectedPetTo || null,
					sofrei_erf: selectedSofreiErf,
					sofrei_ende_from: selectedSofreiEndeFrom || null,
					sofrei_ende_to: selectedSofreiEndeTo || null,
					kast_keys: emailKastKeys.length > 0 ? emailKastKeys : null,
					tags: selectedTags,
				}}
			/>
			{/* Reminder Email Modal */}{" "}
			<ReminderEmailModal
				open={isReminderEmailModalOpen}
				onClose={handleCloseReminderEmailModal}
				selectedRecords={selectedItems}
				fromEmail={getUserEmail()}
				getUserDisplayName={getUserDisplayName}
				viewRoute="/series-tracking"
			/>
			{/* Email History Modal */}
			<EmailHistoryModal
				open={isEmailHistoryModalOpen}
				onClose={handleCloseEmailHistoryModal}
				fromEmail={getUserEmail()}
			/>
			{/* Serie Bulk Edit Modal */}
			<SerieBulkEditModal
				open={isSerieBulkEditModalOpen}
				onClose={handleCloseSerieBulkEditModal}
				selectedItems={selectedItemsForSerieBulkEdit}
				onSave={handleSerieBulkEditSave}
				isSaving={isSerieBulkSaving}
				processingProgress={serieBulkProgress}
				getUserDisplayName={getUserDisplayName}
			/>
			{/* Series Tracking Tour */}
			<SerieTrackingJoyrideTour
				isOpen={isTourOpen}
				onClose={handleCloseTour}
			/>
		</div>
	);
};

// Actions Dropdown Menu Component
const ActionsDropdownMenu = ({
	selectedItems,
	onOpenReminderEmailModal,
	onOpenEmailHistoryModal,
	onExportCSV,
}) => {
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleItemClick = (action) => {
		handleClose();
		action();
	};
	return (
		<div style={{ position: "relative" }}>
			<CraftButton
				purpose="standard"
				variant="secondary"
				size="s"
				type="button"
				icon-name="chevron-down"
				onClick={handleClick}
			>
				Actions
			</CraftButton>

			{open && (
				<>
					{/* Backdrop to close menu when clicking outside */}
					<div
						onClick={handleClose}
						style={{
							position: "fixed",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 999,
						}}
					/>

					{/* Dropdown menu */}
					<div
						style={{
							position: "absolute",
							top: "100%",
							right: 0,
							marginTop: "4px",
							backgroundColor: "white",
							border: "1px solid #ddd",
							borderRadius: "5px",
							boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
							minWidth: "200px",
							zIndex: 1000,
							overflow: "hidden",
						}}
					>
						{/* Export CSV */}
						<button
							onClick={() => handleItemClick(onExportCSV)}
							style={{
								width: "100%",
								padding: "12px 16px",
								border: "none",
								backgroundColor: "white",
								textAlign: "left",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								gap: "10px",
								fontFamily: "MAN EUROPE_Regular",
								fontSize: "14px",
								transition: "background-color 0.2s",
							}}
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
							onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
						>
							<Download size={16} color="#666" />
							<span>Export to Excel</span>
						</button>

						{/* Divider */}
						<div
							style={{
								height: "1px",
								backgroundColor: "#e5e5e5",
								margin: "4px 0",
							}}
						/>

						{/* Send Reminder Email */}
						<button
							onClick={() => handleItemClick(onOpenReminderEmailModal)}
							disabled={!selectedItems || selectedItems.length === 0}
							style={{
								width: "100%",
								padding: "12px 16px",
								border: "none",
								backgroundColor: "white",
								textAlign: "left",
								cursor:
									!selectedItems || selectedItems.length === 0
										? "not-allowed"
										: "pointer",
								display: "flex",
								alignItems: "center",
								gap: "10px",
								fontFamily: "MAN EUROPE_Regular",
								fontSize: "14px",
								opacity: !selectedItems || selectedItems.length === 0 ? 0.5 : 1,
								transition: "background-color 0.2s",
							}}
							onMouseEnter={(e) => {
								if (selectedItems && selectedItems.length > 0) {
									e.target.style.backgroundColor = "#f5f5f5";
								}
							}}
							onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
						>
							<Mail size={16} color="#666" />
							<span>Send Reminder Email ({selectedItems?.length || 0})</span>
						</button>

						{/* Email History */}
						<button
							onClick={() => handleItemClick(onOpenEmailHistoryModal)}
							style={{
								width: "100%",
								padding: "12px 16px",
								border: "none",
								backgroundColor: "white",
								textAlign: "left",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								gap: "10px",
								fontFamily: "MAN EUROPE_Regular",
								fontSize: "14px",
								transition: "background-color 0.2s",
							}}
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
							onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
						>
							<History size={16} color="#666" />
							<span>Email History</span>
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export default PartsTrackingTool;
